<template>
  <div class="unscheduled-treatment-pg">
    <!-- Toast container for centered toasts -->
    <b-toaster name="b-toaster-top-center" position="top-center"></b-toaster>
    
    <!-- Common Overlay for all loading states with default message -->
    <CommonOverlay
      :show="isLoading"
      :title="loadingTitle"
      :message="loadingMessage"
    />
    
    <h2 class="page-title">
      My Account <p class="text-muted deemphasize"><small>{{this.siteID}}</small></p>
    </h2>

    <b-alert
        show            
        dismissible
        fade
        variant="warning" 
        v-if="syncInProgress || (siteStatus && siteStatus.DataLoadingInProgress === 'TRUE')"
        >
          <h4 class="alert-heading">Your data sync is currently in progress</h4>
          <p>You are free to browse around but we suggest that you do not use Open Dental or try to make any follow ups on Teamio in the meantime</p>          
    </b-alert>

    <!-- Practice logo (teamio.sites.LogoUrl). CAPPED by .site-logo below — this was the one
         logo surface in the app with no size constraint, and an 800x800 image set at
         onboarding filled the viewport (Schuyler, 2026-08-27). Whatever URL a practice has,
         its pixel size must never dictate this page's layout. Pinned by
         cypress/node-tests/my-account-logo-cap.test.js. -->
    <div v-if="siteInfo.LogoUrl" class="site-logo-wrap">
      <img class="site-logo" :src="siteInfo.LogoUrl" alt="Practice logo">
    </div>

    <p/>

    <ErrorDisplay/>

    <!-- ===================== My Account tabs (Team / Credits / Patient Payments) =====================
         The three formerly-stacked sections are split into tabs. Modals live OUTSIDE
         <b-tabs> (below) so they always render regardless of the active tab. Deep-linked
         via ?tab=team|credits|payments (TAB_KEYS + syncTabFromRoute + the activeTab watcher). -->
    <b-tabs v-model="activeTab" content-class="mt-3" class="my-account-tabs">
      <!-- ===================== Tab 1: Team ===================== -->
      <b-tab title="Team">

    <div class="ma-team">

    <!-- Tab header — the design language's eyebrow / display title / lede block, the same
         anatomy PatientPaymentsTab ships (.pp-eyebrow / .pp-title / .pp-lede). Sized DOWN to
         24px: Patient Payments owns its whole route at 34px, but this is a TAB inside a page
         that already carries its own "My Account" <h2>, so the page scale would compete with
         it. <h3> for the same reason — it nests under that h2 rather than replacing it. -->
    <div class="ma-tabhead">
      <div class="ma-tabhead__eyebrow">Team</div>
      <h3 class="ma-tabhead__title">Team members</h3>
      <p class="ma-tabhead__lede">
        Everyone with access to your practice, and what they&rsquo;re allowed to do.
      </p>
    </div>

    <b-row>
      <b-col cols="12" xl="6">
    <b-card class="ma-card mb-4 h-100">
      <div class="ma-card__head">
        <div class="ma-card__head-text">
          <h4 class="ma-card__title mb-0">
            <i class="la la-users mr-2"></i>Employees
          </h4>
          <p class="ma-card__subtitle mb-0">Manage your team members and their access</p>
        </div>
        <!-- variant="primary" (was "dark") so this lands on the SAME scoped .btn-primary rule
             that already paints Save Permissions in $pp-brand — the tab's two calls-to-action
             are now one button, not a black one and a teal one. The plus icon drops
             text-success with it: a green glyph on a brand-teal fill was a third colour. -->
        <b-button
          v-if="$can('add','employee') && !isDemoSession"
          variant="primary"
          size="sm"
          class="ma-card__action"
          @click="showAdd=true"
        >
          <i class="fa fa-plus mr-xs mb-xs" /> Add Employee
        </b-button>
      </div>

    <!--
    <b-button      
      v-if="this.user.Role === 'Super Admin' || this.user.Role === 'Admin'"
      variant="dark"
      class="btn create-new-list ml-5 mb-5"      
      @click="showModal('syncdata')"
    >
      <i class="fa fa-plus text-success mr-xs mb-xs" /> Sync Data
    </b-button>
    -->

    <!-- Hidden: Re-calculate Huddle button removed from UI
    <b-button
      variant="dark"
      class="btn create-new-list ml-5 mb-5"
      @click="recalculateHuddle"
    >
      <i class="fa fa-plus text-success mr-xs mb-xs" /> Re-calculate Previous Day's Huddle data
    </b-button>
    -->

    <!--
    <b-button      
      variant="dark"
      class="btn create-new-list ml-5 mb-5"      
      @click="clearFollowupCache"
    >
      <i class="fa fa-plus text-success mr-xs mb-xs" /> Clear Follow-up cache
    </b-button>
    -->

    <b-row v-if="showAdd">
      <!-- start of Forms -->
      <b-col sm="12" md="12" lg="8" xl="6" class="px-xl-3 mb-3">
        <b-card class="border-0 overdue-recall-table-section">
          <div>
            <p class="fw-semi-bold mb-1 w-100 px-2 py-1 fs-14 plan_from">Select Employee</p>
            <multiselect
                  v-if="openDentalEmployees"
                  v-model="selectedEmployee"
                  :options="openDentalEmployees"
                  :multiple="false"
                  :taggable="false"
                  :show-labels="false"
                  :custom-label="employeeCustomLabel"
                  label="FirstName"
                  track-by="UserNum"
                  aria-placeholder="Select an Employee"
                >
                  <template slot="singleLabel" slot-scope="props">
                    <span class="custom__tag">
                      <img
                        class="option__image"
                        src="../../assets/icons-user-40.png"
                        alt="Team member image"
                      />
                      <span>{{ employeeCustomLabel(props.option) }}</span>
                      <i tabindex="1" class="multiselect__tag-icon" @click="removeProvider(props.option)"></i>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <img
                      class="option__image"
                      src="../../assets/icons-user-40.png"
                      alt="Team member image"
                    />
                    <div class="option__desc">
                      <span class="option__title">{{ employeeCustomLabel(props.option) }}</span>
                    </div>
                  </template>
                </multiselect>

                <p class="fw-semi-bold mb-1 w-100 px-2 py-1 fs-14 plan_from">Select Role</p>
                <multiselect
                  v-if="roles"
                  v-model="selectedRole"
                  :options="roles"
                  :multiple="false"
                  :taggable="false"
                  :show-labels="false"                  
                  label="Role"
                  track-by="Role"
                  aria-placeholder="Select a Role"
                />

                <label for="newEmployeeEmail" class="mt-2">
                  <span class="fw-semi-bold">
                    Email:
                    <span class="deemphasize text-muted text-ellipsis">
                      (Employee can sign in with Google or Microsoft using this email)
                    </span>
                  </span>
                </label>
                <b-form-input
                  id="newEmployeeEmail"
                  class="w-50"
                  v-model="userName"
                  type="email"
                  placeholder="employee@company.com"
                  aria-label="Employee login email"
                ></b-form-input>

                <b-button variant="success" type="submit" class="btn-sm mt-2" @click="_addEmployee">ADD</b-button>
                <b-button variant="gray" type="submit" class="btn-sm mt-2 ml-2" @click="showAdd=false">Cancel</b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>

      <div class="table-responsive employees-table mt-3">
        <v-client-table v-if="allSettings && teamioEmployees" :data="teamioEmployees" :columns="columns" :options="options" ref="tb">        
          <!-- Define column header templates -->
          <div slot="h__Actions" class="text-center">Actions</div>

          <!-- Define column cell templates -->
          <!-- Role renders as a pill (the shared My Account design language's .pp-pill
               shape). Admin + Super Admin carry the brand tone because those are the roles
               that can change other people's access; everything else stays neutral. -->
          <div slot="Role" slot-scope="props">
            <span class="ma-pill" :class="{ 'ma-pill--ok': isPrivilegedRole(props.row.Role) }">{{ props.row.Role }}</span>
          </div>

          <div slot="Actions" slot-scope="props" class="text-center ma-actions-cell">
            <b-button v-if="$can('update','employee') && !isDemoSession" variant="light" size="sm" class="ma-action-btn" @click="openEditModal(props.row)" title="Edit" v-b-tooltip.hover>
              <i class="la la-pen"></i>
            </b-button>

            <b-button v-if="$can('delete','employee') && !isDemoSession" variant="light" size="sm" class="ma-action-btn ma-action-btn--danger" @click="showModal('delete', props.row.EmployeeID)" title="Delete" v-b-tooltip.hover>
              <i class="la la-trash"></i>
            </b-button>
          </div>
        </v-client-table>
        </div>
      </b-card>
      </b-col>

      <b-col v-if="user && (user.Role === 'Admin' || user.Role === 'Super Admin')" cols="12" xl="6">
    <b-card class="mb-4 feature-permissions-card ma-card h-100" body-class="d-flex flex-column">
      <div class="ma-card__head">
        <div class="ma-card__head-text">
          <h4 class="ma-card__title mb-0">
            <i class="la la-user-shield mr-2"></i>Feature Permissions
          </h4>
          <p class="ma-card__subtitle mb-0">Control what each role can see and do (scroll to see all features)</p>
        </div>
      </div>
      
      <!-- Permission Change Notice -->
      <b-alert show variant="info" class="mb-3">
        <i class="fa fa-info-circle mr-2"></i>
        <strong>Important:</strong> Permission changes will only take effect after users log out and log back into TeamIO.
      </b-alert>
      
      <!-- Single table + sticky header in ONE scroll container: the header stays pinned on
           vertical scroll, and on narrow screens the whole table scrolls horizontally with
           header + body always aligned (no clipped columns). -->
      <div class="mb-3 feature-permissions-wrapper">
        <table class="table table-bordered table-sm feature-permissions-table">
          <thead>
            <tr>
              <th>Feature</th>
              <!-- Generate role columns dynamically -->
              <th v-for="role in roleNames" :key="role">{{ role }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- One row per feature -->
            <tr v-for="feature in featureList" :key="feature.key">
              <td>{{ feature.label }}</td>
              <!-- For each role, show permission dropdown -->
              <td v-for="role in roleNames" :key="role + '-' + feature.key">
                <select
                  class="form-control form-control-sm"
                  :value="getFeaturePermission(role, feature.key)"
                  @change="setFeaturePermission(role, feature.key, $event.target.value)"
                >
                  <option v-for="level in feature.levels" :key="level" :value="level">{{ level }}</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="feature-permissions-actions">
        <b-button variant="primary" size="sm" class="mr-2" @click="saveFeaturePermissions" :disabled="isSavingFeaturePermissions">
          <i class="fa fa-save mr-1"></i>
          Save Permissions
        </b-button>
        <span v-if="featurePermissionsSaved" class="text-success ml-2">Saved!</span>
        <span v-if="featurePermissionsError" class="text-danger ml-2">Error saving permissions</span>
      </div>
    </b-card>
      </b-col>
    </b-row>

    </div>

      </b-tab>

      <!-- ===================== Tab 2: Credits (all channels) ===================== -->
      <!-- Always shown. SmsCreditsTab renders the live per-channel balances (SMS · Email,
           AI later) for SMS-enabled sites and a static preview/upsell for sites without
           texting (no wallet query in that case). Deep-link key stays ?tab=credits. -->
      <b-tab title="Credits">
        <DemoUnavailable v-if="isDemoSession" />
        <SmsCreditsTab v-else />
      </b-tab>

      <!-- ===================== Tab 3: Patient Payments ===================== -->
      <b-tab title="Patient Payments">
        <!-- Open Dental / Moolah payment oversight. Admin-only, and only on sites
             where OD write-back is enabled (TEXT_TO_PAY_OD_WRITE_ENABLED). The
             write-back is Moolah-only (link + in-office terminal), so
             Stripe/CardPointe payments never get an OdSyncStatus and never appear.
             Redesigned 2026-08-15 onto the Teamio design system — see
             components/PatientPayments/. -->
        <PatientPaymentsTab
          v-if="showOdSyncCard"
          :site-id="siteID"
          :timezone="siteTimezone"
        />

        <b-card v-else class="border-0 mb-4">
          <p class="text-muted mb-0">
            <i class="fa fa-info-circle mr-2"></i>
            Patient payment sync isn't enabled for this practice.
          </p>
        </b-card>
      </b-tab>

      <!-- ===================== Tab 4: WhatsApp =====================
           Self-start WhatsApp signup. Must stay LAST — TAB_KEYS maps positionally.
           Admin/Manager and above, and dark until VUE_APP_WHATSAPP_SELF_START is set (see
           showWhatsAppTab), so prod sees nothing until the flag is turned on.
           ⚠️ ALWAYS REGISTERED, hidden via title-item-class — NEVER v-if this tab:
           BootstrapVue 2.0's BTabs mis-advances the active index when a pane
           unregisters/re-registers, and `user` churns during cold boots, so a
           v-if here made the page walk itself Credits→Payments→WhatsApp
           (reproduced live 2026-08-19; one simulated role flicker = one bump). -->
      <b-tab :title-item-class="showWhatsAppTab ? '' : 'd-none'" title="WhatsApp">
        <WhatsAppTab v-if="showWhatsAppTab" />
      </b-tab>

      <!-- ===================== Tab 5: Billing =====================
           What the practice pays Teamio: plan, card on file, invoices; update card / billing details on Stripe's
           secure pages. The tab is VISIBLE TO EVERYONE (Saumil, 2026-10-14: billing is something anybody may look
           for); only Admin + Super Admin get the content (the server returns 403 to anyone else) and everyone
           else gets a short "Admins only" notice. Must stay LAST — TAB_KEYS maps positionally. NEVER v-if the
           <b-tab> (BootstrapVue 2.0 mis-advances the active index). The CONTENT is v-if'd and the tab is `lazy`,
           so a non-admin never mounts BillingTab (no billing request) and an admin's Stripe reads happen only
           when they open it. The notice waits for `user` so an Admin never sees it flash while the user loads. -->
      <b-tab title="Billing" lazy>
        <DemoUnavailable v-if="isDemoSession" />
        <BillingTab v-else-if="isAdminOrAbove" />
        <BillingAdminOnly v-else-if="user" />
      </b-tab>
    </b-tabs>

    <b-modal ref="delete" id="delete" title="Delete Employee" body-class="bg-white">
      <p>Are you sure you want to remove this employee? They will no longer be able to login to Teamio</p>
      <div slot="modal-footer" class="w-100">
        <b-button variant="danger" size="sm" class="float-right" @click="hideModal('delete')">No</b-button>
        <b-button variant="success" size="sm" class="float-right mr-3" @click="del">Yes</b-button>
      </div>
    </b-modal>

    <b-modal 
      ref="editModal" 
      id="editModal" 
      title="Edit Employee" 
      hide-footer
      body-class="p-0"
      dialog-class="teamio-modal"
      header-bg-variant="white"
      header-text-variant="dark"
      header-border-variant="light"
    >
      <div class="teamio-modal-content p-4">
        <h4 class="teamio-modal-subtitle mb-4">Update Employee Information</h4>
        
        <div class="employee-edit-form">
          <div class="form-group">
            <label class="teamio-form-label">Employee Name</label>
            <b-form-input v-model="editEmployee.Name" disabled class="teamio-input"></b-form-input>
          </div>
          
          <div class="form-group">
            <label class="teamio-form-label">Username</label>
            <b-form-input v-model="editEmployee.Username" disabled class="teamio-input"></b-form-input>
          </div>
          
          <div class="form-group">
            <label class="teamio-form-label">Role</label>
            <multiselect
              v-if="roles"
              v-model="editRole"
              :options="roles"
              :multiple="false"
              :taggable="false"
              :show-labels="false"
              label="Role"
              track-by="Role"
              placeholder="Select a role"
              class="teamio-select"
            />
          </div>
        </div>
        
        <div class="teamio-modal-actions mt-4">
          <b-button variant="outline-secondary" @click="hideModal('editModal')" class="mr-2">Cancel</b-button>
          <b-button variant="primary" @click="updateEmployee" class="px-4">Update</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal ref="syncdata" id="syncdata" title="Sync Data" body-class="bg-white">
      <p>Are you sure you want to request syncing your open dental data with Teamio?</p>
      <p>We ask that you do not use open dental or Teamio in the meantime and log in to the system after about 20-30 mins</p>
      <!--
      <p>You will see the status of your Sync here on this page</p>
      -->
      <div slot="modal-footer" class="w-100">
        <b-button variant="danger" size="sm" class="float-right" @click="hideModal('syncdata')">No</b-button>
        <b-button variant="success" size="sm" class="float-right mr-3" @click="sync">Yes</b-button>
      </div>
    </b-modal>



    <!-- Delete Employee Warning Modal -->
    <b-modal ref="deleteEmployeeWarning" id="deleteEmployeeWarning" title="Delete Employee - Pre-deletion Check" size="xl" body-class="bg-white" no-close-on-backdrop>
      <!-- Progress Indicator (shown during verification) -->
      <div v-if="isCheckingTasks && !verificationComplete" class="mb-3">
        <div class="progress" style="height: 3px;">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" role="progressbar" style="width: 100%"></div>
        </div>
        <p class="text-center text-muted small mb-0 mt-2">
          <b-spinner small></b-spinner>
          Running pre-deletion checks...
        </p>
      </div>
      
      <!-- Employee Info Header -->
      <div class="mb-4 pb-3 border-bottom">
        <p class="mb-0 text-muted">Preparing to remove:</p>
        <h5 class="mb-0 font-weight-bold">{{ employeeToDelete ? employeeToDelete.Name : 'Employee' }}</h5>
      </div>

      <!-- Verification Steps -->
      <div class="verification-steps mb-4">
        <!-- Step 1: Tasks -->
        <div class="verification-step" :class="{'step-checking': verificationSteps.tasks.status === 'checking', 'step-complete': verificationSteps.tasks.status === 'complete', 'step-error': verificationSteps.tasks.status === 'error'}">
          <div class="step-header">
            <div class="step-icon">
              <b-spinner small v-if="verificationSteps.tasks.status === 'checking'"></b-spinner>
              <i v-else-if="verificationSteps.tasks.status === 'complete'" class="fa fa-check-circle text-success"></i>
              <i v-else-if="verificationSteps.tasks.status === 'error'" class="fa fa-exclamation-circle text-danger"></i>
              <i v-else class="fa fa-circle-o text-muted"></i>
            </div>
            <div class="step-content">
              <strong>{{ verificationSteps.tasks.message }}</strong>
              <div v-if="verificationSteps.tasks.status === 'complete' && verificationSteps.tasks.details.total > 0" class="step-details mt-2">
                <div class="alert alert-info mb-2 py-2 px-3">
                  <i class="fa fa-tasks mr-2"></i>
                  <strong>Found {{ verificationSteps.tasks.details.total }} task(s) involving this employee</strong>
                </div>
                <ul class="list-unstyled mb-0 ml-4">
                  <li v-if="verificationSteps.tasks.details.singleAssignee > 0" class="text-warning">
                    <i class="fa fa-arrow-right mr-2"></i>
                    <strong>{{ verificationSteps.tasks.details.singleAssignee }}</strong> task(s) with ONLY this employee
                    
                    <!-- Preview link for single-assignee tasks -->
                    <div class="mt-2">
                      <a href="#" @click.prevent="showSingleAssigneePreview = !showSingleAssigneePreview" class="task-preview-link small">
                        <i :class="showSingleAssigneePreview ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
                        {{ showSingleAssigneePreview ? 'Hide' : 'Show' }} tasks ({{ verificationSteps.tasks.details.singleAssignee }})
                      </a>
                      
                      <!-- Scrollable task table with styled employee tags -->
                      <div v-if="showSingleAssigneePreview" class="task-list-container">
                        <div class="task-list-scrollable">
                          <table class="table table-sm table-hover mb-0 task-preview-table">
                            <thead>
                              <tr>
                                <th style="width: 140px;">Due Date</th>
                                <th>Task Content</th>
                                <th style="width: 200px;">Assigned To</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="task in tasksRequiringAction" :key="task.id">
                                <td class="text-muted small">{{ task.dueAtStr || 'No due date' }}</td>
                                <td>
                                  <div class="task-text">{{ formatTaskContent(task, 120) }}</div>
                                  <div class="task-assignees">
                                    <strong>Assignees:</strong> {{ getAssigneeNames(task) }}
                                  </div>
                                </td>
                                <td>
                                  <span class="badge badge-assignee">
                                    <i class="fa fa-users mr-1"></i>{{ getAssigneeNames(task) }}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <div v-if="tasksRequiringAction.length === 0" class="text-center text-muted py-3">
                            No tasks to display
                          </div>
                        </div>
                        <div v-if="tasksRequiringAction.length > 2" class="scroll-indicator">
                          <i class="fa fa-chevron-down"></i>
                          <span>Scroll for more</span>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li v-if="verificationSteps.tasks.details.multiAssignee > 0" class="text-success">
                    <i class="fa fa-arrow-right mr-2"></i>
                    <strong>{{ verificationSteps.tasks.details.multiAssignee }}</strong> task(s) with other assignees. 
                    Only <strong>{{ employeeToDelete ? employeeToDelete.Name : 'this employee' }}</strong> will be removed from these tasks.
                    
                    <!-- Preview link for multi-assignee tasks -->
                    <div class="mt-2">
                      <a href="#" @click.prevent="showMultiAssigneePreview = !showMultiAssigneePreview" class="task-preview-link small">
                        <i :class="showMultiAssigneePreview ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
                        {{ showMultiAssigneePreview ? 'Hide' : 'Show' }} tasks ({{ verificationSteps.tasks.details.multiAssignee }})
                      </a>
                      
                      <!-- Scrollable task table with styled employee tags -->
                      <div v-if="showMultiAssigneePreview" class="task-list-container">
                        <div class="task-list-scrollable">
                          <table class="table table-sm table-hover mb-0 task-preview-table">
                            <thead>
                              <tr>
                                <th style="width: 140px;">Due Date</th>
                                <th>Task Content</th>
                                <th style="width: 200px;">Assigned To</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="task in multiAssigneeTasks" :key="task.id">
                                <td class="text-muted small">{{ task.dueAtStr || 'No due date' }}</td>
                                <td>
                                  <div class="task-text">{{ formatTaskContent(task, 120) }}</div>
                                  <div class="task-assignees">
                                    <strong>Assignees:</strong> {{ getAssigneeNames(task) }}
                                  </div>
                                </td>
                                <td>
                                  <span class="badge badge-assignee">
                                    <i class="fa fa-users mr-1"></i>{{ getAssigneeNames(task) }}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <div v-if="multiAssigneeTasks.length === 0" class="text-center text-muted py-3">
                            No tasks to display
                          </div>
                        </div>
                        <div v-if="multiAssigneeTasks.length > 2" class="scroll-indicator">
                          <i class="fa fa-chevron-down"></i>
                          <span>Scroll for more</span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
              <div v-else-if="verificationSteps.tasks.status === 'complete' && verificationSteps.tasks.details.total === 0" class="step-details mt-2">
                <div class="alert alert-success mb-0 py-2 px-3">
                  <i class="fa fa-check mr-2"></i>
                  No tasks assigned to this employee
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Workflows -->
        <div class="verification-step" :class="{'step-checking': verificationSteps.workflows.status === 'checking', 'step-complete': verificationSteps.workflows.status === 'complete'}">
          <div class="step-header">
            <div class="step-icon">
              <b-spinner small v-if="verificationSteps.workflows.status === 'checking'"></b-spinner>
              <i v-else-if="verificationSteps.workflows.status === 'complete'" class="fa fa-check-circle text-success"></i>
              <i v-else-if="verificationSteps.workflows.status === 'error'" class="fa fa-exclamation-circle text-danger"></i>
              <i v-else class="fa fa-circle-o text-muted"></i>
            </div>
            <div class="step-content">
              <strong>{{ verificationSteps.workflows.message }}</strong>
              <div v-if="verificationSteps.workflows.status === 'complete' && verificationSteps.workflows.details.total > 0" class="step-details mt-2">
                <!-- ACTION REQUIRED Banner -->
                <div class="alert alert-warning mb-2 py-2 px-3" style="border-left: 4px solid #ff6b6b;">
                  <div class="d-flex align-items-center">
                    <i class="fa fa-exclamation-triangle mr-2" style="font-size: 1.2em;"></i>
                    <div>
                      <strong>⚠️ ACTION REQUIRED:</strong> Found {{ verificationSteps.workflows.details.total }} workflow(s) that reference this employee
                      <div class="small text-muted mt-1">You must choose how to handle these workflows before deleting this employee</div>
                    </div>
                  </div>
                </div>
                
                <!-- Workflow list - ALWAYS SHOWN (not collapsed) -->
                <div class="mt-2 ml-4">
                  <div class="mb-3 p-3" style="background-color: #f8f9fa; border-radius: 4px; border: 1px solid #dee2e6;">
                    <div class="mb-2"><strong>Affected Workflows:</strong></div>
                    <ul class="list-unstyled mb-3">
                      <li v-for="workflow in affectedWorkflows" :key="workflow.workflowID" class="workflow-item mb-2">
                        <i class="fa fa-random mr-2 text-info"></i>
                        <strong>{{ workflow.name }}</strong>
                      </li>
                    </ul>
                    
                    <!-- Workflow handling options - REQUIRED -->
                    <div class="task-options-group mt-3 p-3" style="background-color: #fff; border-radius: 4px; border: 2px solid #ffc107;">
                      <div class="mb-2">
                        <strong style="color: #ff6b6b;">
                          <i class="fa fa-hand-o-right mr-2"></i>
                          What would you like to do with these workflows? *
                        </strong>
                        <div class="small text-danger mt-1">* Required - Please select one option</div>
                      </div>
                      <div class="custom-control custom-radio mt-2 ml-4">
                        <input type="radio" id="workflow-leave-as-is" name="workflowHandling" class="custom-control-input" value="leave-as-is" v-model="workflowHandlingOption">
                        <label class="custom-control-label" for="workflow-leave-as-is">
                          Leave workflows as-is (I will update them manually)
                        </label>
                      </div>
                      <div class="custom-control custom-radio mt-2 ml-4">
                        <input type="radio" id="workflow-reassign" name="workflowHandling" class="custom-control-input" value="reassign" v-model="workflowHandlingOption">
                        <label class="custom-control-label" for="workflow-reassign">
                          Reassign to another employee
                        </label>
                      </div>
                      
                      <!-- Employee selector for reassignment -->
                      <div v-if="workflowHandlingOption === 'reassign'" class="mt-3 ml-4 p-2" style="background-color: #f8f9fa; border-radius: 4px;">
                        <label for="workflow-employee-select"><strong style="color: #ff6b6b;">Select employee: *</strong></label>
                        <select id="workflow-employee-select" v-model="workflowNewAssigneeID" class="form-control form-control-sm" :class="{'border-danger': !workflowNewAssigneeID}">
                          <option :value="null">-- Select Employee --</option>
                          <option v-for="emp in otherEmployees" :key="emp.EmployeeID" :value="emp.EmployeeID">
                            {{ emp.Name }}
                          </option>
                        </select>
                        <div v-if="workflowHandlingOption === 'reassign' && !workflowNewAssigneeID" class="small text-danger mt-1">
                          <i class="fa fa-arrow-up mr-1"></i>
                          Please select an employee to reassign workflows to
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="verificationSteps.workflows.status === 'complete' && verificationSteps.workflows.details.total === 0" class="step-details mt-2">
                <div class="alert alert-success mb-0 py-2 px-3">
                  <i class="fa fa-check mr-2"></i>
                  No workflows reference this employee
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Chat Rooms -->
        <div class="verification-step" :class="{'step-checking': verificationSteps.chatRooms.status === 'checking', 'step-complete': verificationSteps.chatRooms.status === 'complete'}">
          <div class="step-header">
            <div class="step-icon">
              <b-spinner small v-if="verificationSteps.chatRooms.status === 'checking'"></b-spinner>
              <i v-else-if="verificationSteps.chatRooms.status === 'complete'" class="fa fa-check-circle text-success"></i>
              <i v-else class="fa fa-circle-o text-muted"></i>
            </div>
            <div class="step-content">
              <strong>{{ verificationSteps.chatRooms.message }}</strong>
              <div v-if="verificationSteps.chatRooms.status === 'complete' && verificationSteps.chatRooms.details && verificationSteps.chatRooms.details.totalRooms > 0" class="step-details mt-2">
                <div class="alert alert-info mb-2 py-2 px-3">
                  <i class="fa fa-comments mr-2"></i>
                  <strong>Found {{ verificationSteps.chatRooms.details.totalRooms }} chat room(s)</strong>
                </div>
                <ul class="list-unstyled mb-0 ml-4">
                  <li v-if="verificationSteps.chatRooms.details.emptyRooms > 0" class="text-warning">
                    <i class="fa fa-arrow-right mr-2"></i>
                    <strong>{{ verificationSteps.chatRooms.details.emptyRooms }}</strong> empty room(s) will be deleted
                  </li>
                  <li v-if="verificationSteps.chatRooms.details.roomsWithOthers > 0" class="text-success">
                    <i class="fa fa-arrow-right mr-2"></i>
                    <strong>{{ verificationSteps.chatRooms.details.roomsWithOthers }}</strong> room(s) with other users (employee will be removed, rooms kept)
                  </li>
                </ul>
              </div>
              <div v-else-if="verificationSteps.chatRooms.status === 'complete' && (!verificationSteps.chatRooms.details || verificationSteps.chatRooms.details.totalRooms === 0)" class="step-details mt-2">
                <div class="alert alert-success mb-0 py-2 px-3">
                  <i class="fa fa-check mr-2"></i>
                  Not a member of any chat rooms
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: Practice Pulse (only show if feature is enabled) -->
        <div v-if="isPracticePulseEnabled" class="verification-step" :class="{'step-checking': verificationSteps.practicePulse.status === 'checking', 'step-complete': verificationSteps.practicePulse.status === 'complete'}">
          <div class="step-header">
            <div class="step-icon">
              <b-spinner small v-if="verificationSteps.practicePulse.status === 'checking'"></b-spinner>
              <i v-else-if="verificationSteps.practicePulse.status === 'complete'" class="fa fa-check-circle text-success"></i>
              <i v-else class="fa fa-circle-o text-muted"></i>
            </div>
            <div class="step-content">
              <strong>{{ verificationSteps.practicePulse.message }}</strong>
            </div>
          </div>
        </div>

        <!-- Step 5: Responsible Lists -->
        <div class="verification-step" :class="{'step-checking': verificationSteps.responsibleLists.status === 'checking', 'step-complete': verificationSteps.responsibleLists.status === 'complete'}">
          <div class="step-header">
            <div class="step-icon">
              <b-spinner small v-if="verificationSteps.responsibleLists.status === 'checking'"></b-spinner>
              <i v-else-if="verificationSteps.responsibleLists.status === 'complete'" class="fa fa-check-circle text-success"></i>
              <i v-else class="fa fa-circle-o text-muted"></i>
            </div>
            <div class="step-content">
              <strong>{{ verificationSteps.responsibleLists.message }}</strong>
            </div>
          </div>
        </div>

        <!-- Step 6: Custom Alerts -->
        <div class="verification-step" :class="{'step-checking': verificationSteps.customAlerts.status === 'checking', 'step-complete': verificationSteps.customAlerts.status === 'complete'}">
          <div class="step-header">
            <div class="step-icon">
              <b-spinner small v-if="verificationSteps.customAlerts.status === 'checking'"></b-spinner>
              <i v-else-if="verificationSteps.customAlerts.status === 'complete'" class="fa fa-check-circle text-success"></i>
              <i v-else class="fa fa-circle-o text-muted"></i>
            </div>
            <div class="step-content">
              <strong>{{ verificationSteps.customAlerts.message }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Required Section (only if verification complete) -->
      <div v-if="verificationComplete">
        <div v-if="tasksRequiringAction.length > 0">
          <!-- ACTION REQUIRED Banner -->
          <div class="alert alert-warning mb-3 py-2 px-3" style="border-left: 4px solid #ff6b6b;">
            <div class="d-flex align-items-center">
              <i class="fa fa-exclamation-triangle mr-2" style="font-size: 1.2em;"></i>
              <div>
                <strong>⚠️ ACTION REQUIRED:</strong> 
                <strong>{{ employeeToDelete ? employeeToDelete.Name : 'This employee' }}</strong> is the only assignee on 
                <strong>{{ tasksRequiringAction.length }}</strong> incomplete task(s)
                <div class="small text-muted mt-1">You must choose how to handle these tasks before deleting this employee</div>
              </div>
            </div>
          </div>

          <div class="mb-3 p-3" style="background-color: #f8f9fa; border-radius: 4px; border: 2px solid #ffc107;">
            <div class="mb-2">
              <strong style="color: #ff6b6b;">
                <i class="fa fa-hand-o-right mr-2"></i>
                What would you like to do with these tasks? *
              </strong>
              <div class="small text-danger mt-1">* Required - Please select one option</div>
            </div>
            
            <div class="form-group task-options-group mt-3">
              <div class="custom-control custom-radio mb-2">
                <input type="radio" id="option-delete" value="delete" v-model="taskHandlingOption" class="custom-control-input">
                <label class="custom-control-label" for="option-delete">
                  <strong>Delete all tasks</strong>
                  <small class="d-block text-muted">
                    Permanently remove these {{ tasksRequiringAction.length }} tasks
                  </small>
                </label>
              </div>

              <div class="custom-control custom-radio mb-2">
                <input type="radio" id="option-assign-to-me" value="assign-to-me" v-model="taskHandlingOption" class="custom-control-input">
                <label class="custom-control-label" for="option-assign-to-me">
                  <strong>Assign to me</strong>
                  <small class="d-block text-muted">
                    Re-assign these tasks to {{ user.Name || 'yourself' }}
                  </small>
                </label>
              </div>

              <div class="custom-control custom-radio mb-2">
                <input type="radio" id="option-assign-to-other" value="assign-to-other" v-model="taskHandlingOption" class="custom-control-input">
                <label class="custom-control-label" for="option-assign-to-other">
                  <strong>Assign to someone else</strong>
                  <small class="d-block text-muted">Select employee below</small>
                </label>
              </div>
            </div>
          </div>

          <!-- Show employee dropdown if "assign-to-other" selected -->
          <div v-if="taskHandlingOption === 'assign-to-other'" class="mt-3 ml-4 p-2" style="background-color: #f8f9fa; border-radius: 4px;">
            <label><strong style="color: #ff6b6b;">Select new assignee: *</strong></label>
            <b-form-select 
              v-model="newAssigneeID" 
              :options="otherEmployees"
              text-field="Name"
              value-field="EmployeeID"
              :class="{'border-danger': taskHandlingOption === 'assign-to-other' && !newAssigneeID}"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  -- Select Employee --
                </b-form-select-option>
              </template>
            </b-form-select>
            <div v-if="taskHandlingOption === 'assign-to-other' && !newAssigneeID" class="small text-danger mt-1">
              <i class="fa fa-arrow-up mr-1"></i>
              Please select an employee to reassign tasks to
            </div>
          </div>

          <!-- Task Preview (collapsible) -->
          <div class="mt-3 ml-4">
            <a href="#" @click.prevent="showTaskPreview = !showTaskPreview" class="task-preview-link">
              <i :class="showTaskPreview ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
              Preview affected tasks ({{ tasksRequiringAction.length }})
            </a>
            
            <div v-if="showTaskPreview" class="task-list-container">
              <div class="task-list-scrollable" style="max-height: 200px;">
                <div v-for="task in tasksRequiringAction" :key="task.id" style="font-size: 14px; color: #495057; line-height: 1.6; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #e9ecef;">
                  <strong style="color: #6c757d; font-size: 13px; font-weight: 600;">{{ task.dueAtStr || 'No due date' }}</strong>
                  <div style="color: #212529; font-size: 14px; margin-top: 4px;">{{ formatTaskContent(task, 60) }}</div>
                </div>
              </div>
              <div v-if="tasksRequiringAction.length > 3" class="scroll-indicator">
                <i class="fa fa-chevron-down"></i>
                <span>Scroll for more</span>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <p>Are you sure you want to delete <strong>{{ employeeToDelete ? employeeToDelete.Name : 'this employee' }}</strong>?</p>
          <small class="text-muted">This employee has no incomplete tasks assigned to them.</small>
        </div>
      </div>
      
      <!-- Show message while verification is pending -->
      <div v-else-if="!verificationComplete && !isCheckingTasks" class="text-center py-4">
        <p class="text-muted">
          <i class="fa fa-info-circle mr-2"></i>
          Pre-deletion checks will run automatically...
        </p>
      </div>

      <!-- Scroll Indicator for Modal Body -->
      <div v-if="showModalScrollIndicator" class="modal-scroll-indicator">
        <div class="scroll-hint">
          <i class="fa fa-chevron-down"></i>
          <span>More content below - scroll to continue</span>
          <i class="fa fa-chevron-down"></i>
        </div>
      </div>

      <div slot="modal-footer" class="w-100">
        <b-button variant="secondary" size="sm" class="float-right" @click="hideModal('deleteEmployeeWarning')">
          Cancel
        </b-button>
        <b-button 
          variant="danger" 
          size="sm" 
          class="float-right mr-3" 
          @click="confirmDelete"
          :disabled="isCheckingTasks || !verificationComplete || (tasksRequiringAction.length > 0 && !taskHandlingOption) || (tasksRequiringAction.length > 0 && taskHandlingOption === 'assign-to-other' && !newAssigneeID) || (affectedWorkflows.length > 0 && !workflowHandlingOption) || (affectedWorkflows.length > 0 && workflowHandlingOption === 'reassign' && !workflowNewAssigneeID)"
        >
          Delete Employee
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Multiselect from "vue-multiselect";
import Widget from "@/components/Widget/Widget";
import { mapState, mapActions, mapMutations } from 'vuex';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import mixin from '../../mixin.js';
import moment from 'moment-timezone';
import CommonOverlay from "@/components/CommonOverlay";
import defineAbilitiesFor from '../../config/ability.js'; // Change to default import
import SmsCreditsTab from './components/SmsCreditsTab.vue';
import PatientPaymentsTab from './components/PatientPayments/PatientPaymentsTab.vue';
import WhatsAppTab from '@/pages/MyAccount/components/WhatsAppTab.vue';
import BillingTab from '@/pages/MyAccount/components/BillingTab.vue';
import BillingAdminOnly from '@/pages/MyAccount/components/BillingAdminOnly.vue';
import DemoUnavailable from '@/components/Demo/DemoUnavailable.vue';

// Deep-link tab keys ↔ <b-tab> order (Team, Credits, Patient Payments, WhatsApp, Billing).
// APPEND-ONLY: the mapping is POSITIONAL, so a new key must go on the END and its
// <b-tab> must be the LAST tab — inserting anywhere else silently repoints every
// existing ?tab= link. The WhatsApp tab is conditional (showWhatsAppTab); when hidden it stays
// REGISTERED (d-none), so indexes never shift. Billing is visible to everyone; its CONTENT is Admin-only.
const TAB_KEYS = ['team', 'credits', 'payments', 'whatsapp', 'billing'];

export default {
  name: "MyAccount",
  components: { Widget, Multiselect, ErrorDisplay, CommonOverlay, SmsCreditsTab, PatientPaymentsTab, WhatsAppTab, BillingTab, BillingAdminOnly, DemoUnavailable },
  mixins: [mixin],  created() {
    // Resolve ?tab= BEFORE init() — init reads the active tab to decide whether the
    // "Loading Employees" overlay should show (Team tab only; the fetch itself is
    // always needed because other pages read employees from the store).
    this.syncTabFromRoute();
    if(this.allSettings) {
      // init() deliberately NOT called here: the allSettings immediate watcher has
      // ALREADY run it (immediate watchers fire before created), so calling again
      // double-fetched employees on every load where settings were cached.
      this.loadFeaturePermissions();
    }
  },

  mounted() {

  },

  data() {
    return {
      // Which My Account tab is active (0=Team, 1=Credits, 2=Patient Payments).
      // Deep-linked via ?tab=team|credits|payments (see TAB_KEYS + syncTabFromRoute).
      activeTab: 0,
      // Patient payments oversight now lives in
      // components/PatientPayments/PatientPaymentsTab.vue, which owns its own
      // range/filter state and fetching. MyAccount only decides whether to
      // render it (showOdSyncCard) and hands it the site + timezone.

      account: [],
      columns: ['Name', 'Username', 'Role', 'Actions'],
      selectedEmployee: [],
      selectedRole: null,
      userName: '',
      showAdd: false,
      syncInProgress: false,
      editEmployee: {},
      editRole: null,
      isLoading: false,
      loadingTitle: 'Loading',
      loadingMessage: 'Please wait...',
      options: {
        filterable: [],
        skin:'table',
        sortable: [],
        perPage: 10,
        sortByColumn: false,
        pagination: { chunk: 10, dropdown: false },
      },
      featurePermissions: {},
      isSavingFeaturePermissions: false,
      featurePermissionsSaved: false,
      featurePermissionsError: false,
      // Each entry declares the level set the UI should offer for that feature.
      // Level set is determined by which CASL actions are actually checked in code
      // — offering levels that map to ungated actions creates "permission theater"
      // where the dropdown changes nothing the user can see.
      //   - None / Read           → only `view`/`read` is gated anywhere
      //   - None / Read / Update  → above + `update`/`edit`/`add` is gated
      //   - None / Read / Update / Delete → all above + `delete` is gated
      //   - None / Update         → inline action with no separate "view" gate
      //
      // Removed entries from previous versions:
      //   - 'sms-conversations'  — dead key; the real subject is `conversations`
      //   - 'Page'               — conditional rule (CreatedByEmployeeID); excluded
      //                            from overrides in ability.js, so a UI entry would
      //                            silently do nothing
      //   - 'chatroom'           — niche admin action; kept hardcoded in ability.js
      featureList: [
        { key: 'team-insights', label: 'Team Insights', levels: ['None', 'Read'] },
        { key: 'huddle', label: 'Huddle', levels: ['None', 'Read'] },
        { key: 'kanban', label: 'Kanban', levels: ['None', 'Read'] },
        { key: 'tasks', label: 'Tasks', levels: ['None', 'Read'] },
        { key: 'chat', label: 'Chat', levels: ['None', 'Read'] },
        { key: 'conversations', label: 'SMS Conversations', levels: ['None', 'Read'] },
        { key: 'wiki', label: 'Wiki', levels: ['None', 'Read'] },
        { key: 'forms', label: 'Forms', levels: ['None', 'Read', 'Update', 'Delete'] },
        { key: 'asap_list', label: 'ASAP List', levels: ['None', 'Read'] },
        { key: 'overdue_recall', label: 'Overdue Recall', levels: ['None', 'Read'] },
        { key: 'unscheduled_treatment', label: 'Unscheduled Treatment', levels: ['None', 'Read'] },
        { key: 'outstanding_claim', label: 'Outstanding Claim', levels: ['None', 'Read'] },
        { key: 'broken_appointment', label: 'Broken Appointment', levels: ['None', 'Read'] },
        { key: 'patient_balances', label: 'Patient Balances', levels: ['None', 'Read'] },
        { key: 'outstanding_preauths', label: 'Outstanding Preauths', levels: ['None', 'Read'] },
        { key: 'settings', label: 'Settings', levels: ['None', 'Read'] },
        { key: 'kpi', label: 'KPI', levels: ['None', 'Read'] },
        { key: 'practice-pulse', label: 'Practice Pulse', levels: ['None', 'Read'] },
        { key: 'workflow', label: 'Workflow', levels: ['None', 'Read', 'Update', 'Delete'] },
        { key: 'campaigns', label: 'Campaigns', levels: ['None', 'Read'] },
        // Insurance Verification is the one row here that gates SPENDING, not just
        // viewing — every action on that page is a billed Stedi eligibility check.
        // 'None' is how an office keeps a role off the money button.
        { key: 'insurance-verification', label: 'Insurance Verification', levels: ['None', 'Read'] },
        { key: 'myaccount', label: 'My Account', levels: ['None', 'Read'] },
        { key: 'employee', label: 'Employee Management', levels: ['None', 'Read', 'Update', 'Delete'] },
        { key: 'followupDate', label: 'Follow-up Date', levels: ['None', 'Update'] },
        { key: 'followupNote', label: 'Follow-up Note', levels: ['None', 'Update'] },
      ],
      // Employee deletion with task handling
      selectedEmployeeID: null,
      employeeToDelete: null,
      tasksRequiringAction: [],
      multiAssigneeTasks: [], // Tasks that have other assignees (will be kept)
      taskHandlingOption: null, // MUST be explicitly selected - no default assumptions
      newAssigneeID: null,
      showTaskPreview: false,
      showMultiAssigneePreview: false,
      showSingleAssigneePreview: false,
      isCheckingTasks: false,
      // Workflow handling
      affectedWorkflows: [],
      workflowHandlingOption: null, // Options: 'reassign' or 'leave-as-is' - MUST be explicitly selected
      workflowNewAssigneeID: null,
      showWorkflowPreview: false,
      // Verification steps tracking
      verificationSteps: {
        tasks: { status: 'pending', message: '', details: {} },
        workflows: { status: 'pending', message: '', details: {} },
        chatRooms: { status: 'pending', message: '', details: {} },
        practicePulse: { status: 'pending', message: '' },
        responsibleLists: { status: 'pending', message: '' },
        customAlerts: { status: 'pending', message: '' }
      },
      verificationComplete: false,
      showModalScrollIndicator: false
    };
  },
  computed: {
    /*
     ...mapGetters({

      }),
    */

    siteInfo() {
      return this.getSite(this.siteID);
    },

    // Show the OD payment-sync oversight card only to admins, and only when
    // OD write-back is enabled for this site (mirrors the practicePulsePaidFor
    // boolean-setting check pattern in the mixin).
    isAdminOrAbove() {
      return !!(this.user && (this.user.Role === 'Admin' || this.user.Role === 'Super Admin'));
    },

    odWriteEnabled() {
      return !!(this.allSettings
        && this.allSettings.TEXT_TO_PAY_OD_WRITE_ENABLED
        && String(this.allSettings.TEXT_TO_PAY_OD_WRITE_ENABLED.PropertyValue).toUpperCase() === 'TRUE');
    },

    showOdSyncCard() {
      return this.isAdminOrAbove && this.odWriteEnabled;
    },

    // Who may run the WhatsApp wizard: Admin, Super Admin AND Manager — the same three roles the
    // backend admits (conversations-service lib/siteAuth). Deliberately NOT isAdminOrAbove: the
    // first real operator (Barbados) is a Manager. Must match ConversationsSettings.vue's copy
    // (conversations-settings-whatsapp-crosslink.test.js AGREEMENT).
    canRunWhatsAppWizard() {
      const role = this.user && this.user.Role;
      return role === 'Admin' || role === 'Super Admin' || role === 'Manager';
    },

    // WhatsApp self-start tab — Admin/Manager and above, dark until VUE_APP_WHATSAPP_SELF_START is set.
    showWhatsAppTab() {
      return !this.isDemoSession && this.canRunWhatsAppWizard && process.env.VUE_APP_WHATSAPP_SELF_START === 'true';
    },

    // Demo site (plan Task 6.5): Credits, Billing, WhatsApp setup and the employee controls are not part of the demo.
    isDemoSession() {
      return !!this.$store && this.$store.getters['customer/isDemo'] === true;
    },

    // Site timezone, passed to the payments tab so its date handling is the
    // practice's day rather than the browser's.
    siteTimezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
    },

    siteStatus() {
      if(this.account && this.account[0] && this.account[0].length > 0)
          return this.account[0][0];      
    },

    teamioEmployees() {
      if(this.account) {
        return this.account[1];
      }
    },

    openDentalEmployees() {
      if(this.account) {
        let teamioEmployeesWithODNum = _.map(this.teamioEmployees, 'ODUserNum');
        return this.account[2].filter(e => !teamioEmployeesWithODNum.includes(e.UserNum));        
      }
    },

    roles() {
      if(this.account) {
        return this.account[3];
      }
    },

    roleNames() {
      // Return array of role names (e.g., ['Manager', 'Employee', ...]) excluding Admin roles
      return this.roles ? this.roles.map(r => r.Role).filter(role => role !== 'Admin' && role !== 'Super Admin') : [];
    },

    otherEmployees() {
      // Get all active employees except the one being deleted
      if (!this.teamioEmployees || !this.selectedEmployeeID) return [];
      return this.teamioEmployees.filter(emp => 
        emp.EmployeeID !== this.selectedEmployeeID && emp.Active === 1
      );
    },

    roleFeaturePairs() {
      // Returns an array of { role, feature } for all combinations
      const pairs = [];
      for (const role of this.roleNames) {
        for (const feature of this.featureList) {
          pairs.push({ role, feature });
        }
      }
      return pairs;
    },
    isPracticePulseEnabled() {
      // Check if Practice Pulse feature is enabled for this site
      return this.allSettings &&
             this.allSettings.PRACTICE_PULSE_PAID_FOR &&
             this.allSettings.PRACTICE_PULSE_PAID_FOR.PropertyValue === 'TRUE';
    },
  },
  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('myaccount', ['getMyAccount', 'deleteEmployee', 'addEmployee', 'syncdata', 'recalculateHuddleCache', 'updateEmployee']),
    ...mapActions('customer', ['getEmployees']),
    ...mapActions('teamio', ['stopTrackingFUAttempt', 'getOdSyncStatusList']),

    // Deep-link: set the active tab from ?tab=team|credits|payments|whatsapp on load.
    syncTabFromRoute() {
      const q = this.$route && this.$route.query ? this.$route.query.tab : null;
      const key = String(q || '').toLowerCase();
      // The WhatsApp pane is always REGISTERED (see the tab's comment) but hidden for
      // non-admins/flag-off — refuse to deep-link into it so those users can't land
      // on an intentionally-empty pane.
      if (key === 'whatsapp' && !this.showWhatsAppTab) return;
      const idx = TAB_KEYS.indexOf(key);
      if (idx >= 0) this.activeTab = idx;
    },

    // Helper method to strip HTML tags and decode entities from task content
    stripHtml(content) {
      if (!content) return '';
      // First remove HTML tags
      const stripped = content.replace(/<[^>]*>/g, '');
      // Then decode HTML entities by using textarea trick
      const textarea = document.createElement('textarea');
      textarea.innerHTML = stripped;
      const decoded = textarea.value;
      // Clean up extra whitespace
      return decoded.replace(/\s+/g, ' ').trim();
    },

    // Format task content for display
    formatTaskContent(task, maxLength = 80) {
      if (!task.content) return 'No content';
      const cleaned = this.stripHtml(task.content);
      if (cleaned.length > maxLength) {
        return cleaned.substring(0, maxLength) + '...';
      }
      return cleaned;
    },

    // Parse task content HTML to extract employees, patients, and plain text
    cleanTaskContent(content) {
      if (!content) return { text: '', employees: [], patients: [], tags: [] };
      
      // Create a temporary div to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // Initialize result object
      const result = {
        text: '',
        employees: [],
        patients: [],
        tags: []
      };

      // Function to process text nodes
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent.trim();
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if this is a badge
          if (node.classList.contains('badge')) {
            if (node.hasAttribute('data-teamio-employeeid')) {
              const employeeId = node.getAttribute('data-teamio-employeeid');
              const employeeName = node.textContent.trim();
              result.employees.push({ id: employeeId, name: employeeName });
              return ''; // Don't include employee name in text
            }
            if (node.hasAttribute('data-teamio-patientid')) {
              const patientId = node.getAttribute('data-teamio-patientid');
              const patientName = node.textContent.trim();
              result.patients.push({ id: patientId, name: patientName });
              return ''; // Don't include patient name in text
            }
            // If it's a badge but not an employee or patient, it's a tag
            const tagText = node.textContent.trim();
            if (tagText) {
              result.tags.push(tagText);
            }
            return ''; // Don't include tag text in main content
          }

          // Process child nodes
          return Array.from(node.childNodes)
            .map(processNode)
            .filter(text => text)
            .join(' ');
        }

        return '';
      };

      // Process all nodes and get text content
      result.text = processNode(tempDiv);

      // Clean up extra whitespace in text
      result.text = result.text.replace(/\s+/g, ' ').trim();

      return result;
    },

    // Get assignee names from employee IDs
    getAssigneeNames(task) {
      if (!task.assignees || task.assignees.length === 0) return 'Unassigned';
      
      const names = task.assignees.map(employeeID => {
        const employee = this.teamioEmployees?.find(emp => emp.EmployeeID === employeeID);
        return employee ? employee.Name : 'Unknown';
      }).filter(name => name !== 'Unknown');
      
      if (names.length === 0) return 'Unassigned';
      if (names.length === 1) return names[0];
      if (names.length === 2) return `${names[0]} & ${names[1]}`;
      
      // For 3+ assignees, show first two and count
      return `${names[0]}, ${names[1]} +${names.length - 2} more`;
    },


    async init() {
      // The blocking overlay is a TEAM-tab concern — a user landing on Credits /
      // Patient Payments / WhatsApp (e.g. the header pill's ?tab=credits deep link)
      // shouldn't stare at "Loading Employees". The employee FETCH still always runs:
      // the Vuex store needs it for other pages (@ mentions, Chat, etc.).
      // Gate on the ROUTE, not activeTab: the allSettings immediate watcher fires
      // BEFORE created() (Vue 2 initWatch order), so activeTab can still be its
      // default 0 here — that ordering hole put the overlay back on deep links.
      const tabParam = String((this.$route && this.$route.query && this.$route.query.tab) || 'team').toLowerCase();
      const showOverlay = tabParam === 'team' || TAB_KEYS.indexOf(tabParam) === -1;
      try {
        if (showOverlay) {
          this.isLoading = true;
          this.loadingTitle = 'Loading Employees';
          this.loadingMessage = 'Please wait while we retrieve employee information...';
        }

        let res = await this.getMyAccount({siteID: this.siteID});
        if(res.data && res.data.length > 0) {
          this.account = res.data;
        }

        
        // Refresh the global employee list from database
        // This ensures the Vuex store has the latest employees for all pages
        // (e.g., Tasks page @ mentions, Chat, etc.)
        await this.getEmployees({
          sqlQuery: `select e.*, r.Role from teamio.employees e, teamio.roles r where e.SiteID='${this.siteID}' and e.RoleID = r.RoleID;`
        });
        
      } catch (error) {
        console.error('Error loading account data:', error);
      } finally {
        this.isLoading = false;
      }
    },

    showModal(name) {      
      this.resetError();
      this.$refs[name].show();
    },

    hideModal(ref) {
      this.$refs[ref].hide();
    },

    async sync() {
      try {
        this.hideModal('syncdata');
        let payload = {
          siteID: this.siteID,
          action: 'Request MySQL Dump'
        }
        await this.syncdata(payload);
        this.syncInProgress = true;
      } catch(e) {
        console.log('Error' + e);
      }
    },

    async clearFollowupCache() {
      console.log('trying to clear follow-up cache');      
    },

    async recalculateHuddle() {
      let payload = {
        SiteID: this.siteID,
        Date: moment().subtract(1, 'days').format('YYYY-MM-DD')
      }
      await this.recalculateHuddleCache(payload);
    },

    async checkTasksBeforeDelete() {
      this.isCheckingTasks = true;
      this.verificationComplete = false;
      this.tasksRequiringAction = [];
      this.multiAssigneeTasks = [];
      this.showTaskPreview = false;
      this.showMultiAssigneePreview = false;
      this.showSingleAssigneePreview = false;
      
      // Reset all verification steps
      this.verificationSteps = {
        tasks: { status: 'checking', message: 'Checking assigned tasks...', details: {} },
        workflows: { status: 'pending', message: 'Checking workflows...', details: {} },
        chatRooms: { status: 'pending', message: 'Checking chat room memberships...', details: {} },
        practicePulse: { status: 'pending', message: 'Checking Practice Pulse settings...' },
        responsibleLists: { status: 'pending', message: 'Checking responsible employee lists...' },
        customAlerts: { status: 'pending', message: 'Checking custom alerts...' }
      };
      
      try {
        // STEP 1: Check Tasks
        console.log(`Checking tasks for employee: ${this.selectedEmployeeID}`);
        
        // Check if tasks are loaded in store, if not, load them first
        const tasksInStore = [
          ...(this.$store.state.tasks.myCreatedTasks || []),
          ...(this.$store.state.tasks.assignedToMeTasks || []),
          ...(this.$store.state.tasks.myEmployeesTasks || [])
        ];
        
        if (tasksInStore.length === 0) {
          this.verificationSteps.tasks.message = 'Loading tasks from database...';
          await this.$store.dispatch('tasks/getTasksForSite', {
            siteID: this.siteID,
            employeeID: this.userID
          });
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        // Get all tasks
        const allTasks = [
          ...(this.$store.state.tasks.myCreatedTasks || []),
          ...(this.$store.state.tasks.assignedToMeTasks || []),
          ...(this.$store.state.tasks.myEmployeesTasks || [])
        ];
        
        // Deduplicate
        const taskMap = new Map();
        allTasks.forEach(task => {
          if (task.id) taskMap.set(task.id, task);
        });
        
        const uniqueTasks = Array.from(taskMap.values());
        
        // Find tasks involving this employee
        const tasksWithEmployee = uniqueTasks.filter(task => {
          if (task.deleted || task.status === 'COMPLETED') return false;
          const assignees = task.assignees || [];
          return assignees.includes(this.selectedEmployeeID) || task.assignedToEmpID === this.selectedEmployeeID;
        });
        
        // Categorize tasks: single-assignee vs multi-assignee
        const singleAssigneeTasks = [];
        const multiAssigneeTasks = [];
        
        tasksWithEmployee.forEach(task => {
          const assignees = task.assignees || [];
          const isOnlyAssignee = (assignees.length === 1 && assignees[0] === this.selectedEmployeeID) ||
                                 (assignees.length === 0 && task.assignedToEmpID === this.selectedEmployeeID);
          
          if (isOnlyAssignee) {
            singleAssigneeTasks.push(task);
          } else {
            multiAssigneeTasks.push(task);
          }
        });
        
        this.tasksRequiringAction = singleAssigneeTasks;
        this.multiAssigneeTasks = multiAssigneeTasks;
        this.verificationSteps.tasks = {
          status: 'complete',
          message: 'Task check complete',
          details: {
            total: tasksWithEmployee.length,
            singleAssignee: singleAssigneeTasks.length,
            multiAssignee: multiAssigneeTasks.length
          }
        };
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // STEP 2: Check Workflows
        this.verificationSteps.workflows.status = 'checking';
        try {
          // Fetch workflows with JsonText and FlowyText to check for employee references
          const response = await this.$store.dispatch('teamio/runQuery', {
            sqlQuery: `SELECT WorkflowID, Name, JsonText, FlowyText FROM teamio.workflow WHERE SiteID='${this.siteID}'`
          });
          
          console.log('Workflow query response:', response);
          
          if (response && response.data && response.data) {
            const allWorkflows = response.data;
            
            // Filter workflows that actually reference this employee
            const workflows = allWorkflows.filter(workflow => {
              if (!workflow.WorkflowID || !workflow.Name) return false;
              
              // Check if employee ID appears in JsonText or FlowyText
              const jsonText = workflow.JsonText || '';
              const flowyText = workflow.FlowyText || '';
              const employeeID = this.selectedEmployeeID;
              
              // Simple string search for employee ID in the JSON
              const inJsonText = jsonText.includes(employeeID);
              const inFlowyText = flowyText.includes(employeeID);
              
              if (inJsonText || inFlowyText) {
                console.log(`✓ Workflow "${workflow.Name}" references employee ${employeeID}`);
                return true;
              }
              
              return false;
            });
            
            this.affectedWorkflows = workflows.map(workflow => ({
              workflowID: workflow.WorkflowID,
              name: workflow.Name,
              jsonText: workflow.JsonText || '',
              flowyText: workflow.FlowyText || ''
            }));
            
            this.verificationSteps.workflows = {
              status: 'complete',
              message: workflows.length > 0 ? `Found ${workflows.length} workflow(s) that reference this employee` : 'No workflows reference this employee',
              details: {
                total: workflows.length
              }
            };
            
            console.log(`✅ Workflow check complete: ${workflows.length} workflow(s) reference employee ${this.selectedEmployeeID}`);
          } else {
            throw new Error('Invalid response structure');
          }
        } catch (error) {
          console.error('❌ Error checking workflows:', error);
          console.error('Error details:', error.response || error.message || error);
          // Don't block deletion if workflow check fails - just skip it
          this.verificationSteps.workflows = {
            status: 'complete',
            message: 'Workflow check skipped (manual verification recommended)',
            details: { total: 0 }
          };
        }
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // STEP 3: Check Chat Rooms
        this.verificationSteps.chatRooms.status = 'checking';
        try {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const db = this.$store.state.db;
          
          const roomsRef = collection(db, 'rooms');
          const roomsQuery = query(roomsRef, where('siteID', '==', this.siteID));
          const roomsSnapshot = await getDocs(roomsQuery);
          
          const roomsWithEmployee = [];
          const emptyRoomsToDelete = [];
          const roomsToKeep = [];
          
          roomsSnapshot.forEach(doc => {
            const room = doc.data();
            if (room.users && Array.isArray(room.users)) {
              const userInRoom = room.users.some(u => u.employeeID === this.selectedEmployeeID);
              if (userInRoom) {
                roomsWithEmployee.push({ id: doc.id, ...room });
                
                // DEFENSIVE: Only mark for deletion if this employee is the ONLY user
                // Count OTHER users (not the deleted employee)
                const otherUsers = room.users.filter(u => u.employeeID !== this.selectedEmployeeID);
                
                if (otherUsers.length === 0) {
                  // This employee is the only user - safe to delete
                  emptyRoomsToDelete.push({ id: doc.id, roomName: room.roomName });
                  console.log(`✓ Room "${room.roomName}" will be deleted (no other users)`);
                } else {
                  // Other users exist - DO NOT delete, just remove this employee
                  roomsToKeep.push({ id: doc.id, roomName: room.roomName, otherUsersCount: otherUsers.length });
                  console.log(`✓ Room "${room.roomName}" will be kept (${otherUsers.length} other user(s))`);
                }
              }
            }
          });
          
          this.verificationSteps.chatRooms = {
            status: 'complete',
            message: 'Chat room check complete',
            details: {
              totalRooms: roomsWithEmployee.length,
              emptyRooms: emptyRoomsToDelete.length,
              roomsWithOthers: roomsToKeep.length
            }
          };
          
          console.log(`Found ${roomsWithEmployee.length} chat rooms with this employee`);
          console.log(`${emptyRoomsToDelete.length} empty rooms will be deleted`);
          console.log(`${roomsToKeep.length} rooms have other users and will be kept`);
        } catch (error) {
          console.error('Error checking chat rooms:', error);
          this.verificationSteps.chatRooms = {
            status: 'complete',
            message: 'Will be removed from chat rooms'
          };
        }
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // STEP 4: Check Practice Pulse (only if feature is enabled)
        if (this.isPracticePulseEnabled) {
          this.verificationSteps.practicePulse.status = 'checking';
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const db = this.$store.state.db;
            
            const practicePulseRef = doc(db, 'practice-pulse', this.siteID);
            const practicePulseDoc = await getDoc(practicePulseRef);
            
            if (practicePulseDoc.exists()) {
              const practicePulse = practicePulseDoc.data();
              const settings = practicePulse.settings || {};
              const providerSettings = settings.providerSettings || {};
              const customAlerts = settings.customAlerts || [];
              
              let found = false;
              let details = [];
              
              // Check if employee is in provider settings
              if (providerSettings[this.selectedEmployeeID]) {
                found = true;
                details.push('Provider settings');
              }
              
              // Check custom alerts
              const alertsWithEmployee = customAlerts.filter(alert => 
                alert.employee && alert.employee.EmployeeID === this.selectedEmployeeID
              );
              
              if (alertsWithEmployee.length > 0) {
                found = true;
                details.push(`${alertsWithEmployee.length} custom alert(s)`);
              }
              
              this.verificationSteps.practicePulse = {
                status: 'complete',
                message: found 
                  ? `Will be removed from: ${details.join(', ')}` 
                  : 'Employee not found in Practice Pulse settings',
                details: { hasSettings: found, items: details }
              };
              
              console.log(`✅ Practice Pulse check complete: ${found ? 'Employee found in settings' : 'Employee not in settings'}`);
            } else {
              this.verificationSteps.practicePulse = {
                status: 'complete',
                message: 'No Practice Pulse data found for this site'
              };
            }
          } catch (error) {
            console.error('Error checking Practice Pulse:', error);
            this.verificationSteps.practicePulse = {
              status: 'complete',
              message: 'Will be removed from Practice Pulse settings (verification skipped)'
            };
          }
        } else {
          this.verificationSteps.practicePulse = {
            status: 'complete',
            message: 'Practice Pulse not enabled for this site'
          };
        }
        
        // STEP 5: Check Responsible Lists
        this.verificationSteps.responsibleLists.status = 'checking';
        try {
          // Check the responsible employee list settings in teamio.settings
          const responsibleListProperties = [
            'OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID',
            'UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID',
            'OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID',
            'OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID',
            'PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID',
            'BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID'
          ];
          
          const listsWithEmployee = [];
          
          for (const property of responsibleListProperties) {
            const setting = this.allSettings[property];
            if (setting && setting.PropertyValue) {
              // PropertyValue is a pipe-separated list of employee IDs
              const employeeList = setting.PropertyValue.split('|');
              if (employeeList.includes(this.selectedEmployeeID)) {
                // Convert property name to human-readable format
                const listName = property
                  .replace('_RESPONSIBLE_EMPLOYEEID', '')
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, l => l.toUpperCase());
                listsWithEmployee.push(listName);
              }
            }
          }
          
          this.verificationSteps.responsibleLists = {
            status: 'complete',
            message: listsWithEmployee.length > 0
              ? `Will be removed from ${listsWithEmployee.length} list(s): ${listsWithEmployee.join(', ')}`
              : 'Employee not found in any responsible employee lists',
            details: { count: listsWithEmployee.length, lists: listsWithEmployee }
          };
          
          console.log(`✅ Responsible Lists check complete: ${listsWithEmployee.length} list(s)`);
        } catch (error) {
          console.error('Error checking responsible lists:', error);
          this.verificationSteps.responsibleLists = {
            status: 'complete',
            message: 'Will be removed from responsible employee lists (verification skipped)'
          };
        }
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // STEP 6: Check Custom Alerts (only if Practice Pulse is enabled)
        this.verificationSteps.customAlerts.status = 'checking';
        if (this.isPracticePulseEnabled) {
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const db = this.$store.state.db;
            
            const practicePulseRef = doc(db, 'practice-pulse', this.siteID);
            const practicePulseDoc = await getDoc(practicePulseRef);
            
            if (practicePulseDoc.exists()) {
              const practicePulse = practicePulseDoc.data();
              const settings = practicePulse.settings || {};
              const customAlerts = settings.customAlerts || [];
              
              // Check custom alerts for this employee
              const alertsWithEmployee = customAlerts.filter(alert => 
                alert.employee && alert.employee.EmployeeID === this.selectedEmployeeID
              );
              
              this.verificationSteps.customAlerts = {
                status: 'complete',
                message: alertsWithEmployee.length > 0
                  ? `Will be removed from ${alertsWithEmployee.length} custom alert(s)`
                  : 'Employee not found in custom alerts',
                details: { count: alertsWithEmployee.length }
              };
              
              console.log(`✅ Custom Alerts check complete: ${alertsWithEmployee.length} alert(s)`);
            } else {
              this.verificationSteps.customAlerts = {
                status: 'complete',
                message: 'No Practice Pulse data found'
              };
            }
          } catch (error) {
            console.error('Error checking custom alerts:', error);
            this.verificationSteps.customAlerts = {
              status: 'complete',
              message: 'Will be removed from custom alerts (verification skipped)'
            };
          }
        } else {
          this.verificationSteps.customAlerts = {
            status: 'complete',
            message: 'Practice Pulse not enabled for this site'
          };
        }
        
        this.verificationComplete = true;
        
      } catch (error) {
        console.error('Error during verification:', error);
        this.$toast.error('Error checking employee data. Please try again.');
        this.verificationSteps.tasks.status = 'error';
        this.verificationSteps.tasks.message = 'Error checking tasks';
      } finally {
        this.isCheckingTasks = false;
      }
    },

    async confirmDelete() {
      // Validate task handling option
      if (this.taskHandlingOption === 'assign-to-other' && !this.newAssigneeID) {
        this.$toast.error('Please select an employee to reassign tasks to');
        return;
      }
      
      // Double-check permission
      if (!this.$can('delete', 'employee')) {
        this.$toast.error('You do not have permission to delete employees');
        return;
      }
      
      // Final confirmation with summary of action
      const employeeName = this.employeeToDelete ? this.employeeToDelete.Name : 'this employee';
      let taskSummary = '';
      
      if (this.tasksRequiringAction.length > 0) {
        const taskCount = this.tasksRequiringAction.length;
        const taskWord = taskCount === 1 ? 'task' : 'tasks';
        
        if (this.taskHandlingOption === 'delete') {
          taskSummary = `\n\n• ${taskCount} ${taskWord} will be PERMANENTLY DELETED`;
        } else if (this.taskHandlingOption === 'assign-to-me') {
          taskSummary = `\n\n• ${taskCount} ${taskWord} will be reassigned to you`;
        } else if (this.taskHandlingOption === 'assign-to-other') {
          const newAssignee = this.otherEmployees.find(emp => emp.EmployeeID === this.newAssigneeID);
          const newAssigneeName = newAssignee ? newAssignee.Name : 'selected employee';
          taskSummary = `\n\n• ${taskCount} ${taskWord} will be reassigned to ${newAssigneeName}`;
        }
      }
      
      const confirmMessage = `You are about to permanently delete:\n${employeeName}${taskSummary}\n\n⚠️ This action CANNOT be undone!\n\nAre you absolutely sure you want to proceed?`;
      
      const confirmed = await this.$bvModal.msgBoxConfirm(
        confirmMessage,
        {
          title: 'Final Confirmation',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes, Delete Employee',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );
      
      if (!confirmed) {
        console.log('User cancelled employee deletion');
        return;
      }
      
      try {
        this.hideModal('deleteEmployeeWarning');
        console.log(`now trying to delete employeeID: ${this.selectedEmployeeID}`);
        
        this.isLoading = true;
        this.loadingTitle = 'Removing Employee';
        this.loadingMessage = 'Please wait while we process your request...';
        
        // Prepare task handling payload
        const payload = {
          siteID: this.siteID,
          employeeID: this.selectedEmployeeID,
          taskHandling: {
            option: this.taskHandlingOption,
            newAssigneeID: this.newAssigneeID,
            currentUserID: this.userID, // For "assign-to-me" option
            tasksCount: this.tasksRequiringAction.length
          },
          workflowHandling: {
            option: this.workflowHandlingOption,
            newAssigneeID: this.workflowNewAssigneeID,
            workflowsCount: this.affectedWorkflows.length
          }
        };
        
        await this.deleteEmployee(payload);
        this.$toast.success("Employee deleted successfully");
        
        // Refresh the employee list
        this.loadingTitle = 'Refreshing Employee List';
        this.loadingMessage = 'Please wait while we update the employee list...';
        await this.init();
        
        // Reset state
        this.tasksRequiringAction = [];
        this.taskHandlingOption = null; // Reset to no selection
        this.newAssigneeID = null;
        this.affectedWorkflows = [];
        this.workflowHandlingOption = null; // Reset to no selection
        this.workflowNewAssigneeID = null;
        this.showTaskPreview = false;
        this.showWorkflowPreview = false;
      } catch(e) {
        this.$toast.error("There was an error while deleting this employee. Please try again later");
        console.log('Error' + e);
      } finally {
        this.isLoading = false;
      }
    },

    async del() {
      // This method is now replaced by confirmDelete
      // Keeping for backward compatibility
      await this.confirmDelete();
    },

    async _addEmployee() {      
      if(this.selectedEmployee.length === 0) {        
        this.$toast.error("Please select an employee first");
        return false;
      }

      if(!this.selectedRole) {        
        this.$toast.error('Please select a Role for this employee');
        return false;
      }

      if(this.userName.trim() === '') {        
        this.$toast.error('Please enter an email address for this employee');
        return false;
      }

      let validEmail = String(this.userName)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );

      if (!validEmail) {        
        this.$toast.error('Please enter a valid mail address for this employee');
        return;
      }

      let employeeName;

      if(this.selectedEmployee.FirstName.trim() === "") {
        employeeName = this.selectedEmployee.UserName;
      } else {
        employeeName = `${this.selectedEmployee.FirstName} ${this.selectedEmployee.LastName}`;
      }      

      let payload = {
        siteID : this.siteID,
        customerID: this.user.CustomerID,
        employeeName: employeeName,
        roleID: this.selectedRole.RoleID,
        userName: this.userName,
        odUserNum: this.selectedEmployee.UserNum,        
      }

      try {
        this.isLoading = true;
        this.loadingTitle = 'Adding New Employee';
        this.loadingMessage = 'Please wait while we set up the new employee account...';
        
        // Set custom overlay message for adding employee
        await this.addEmployee(payload);
        this.$toast.success('Employee added successfully');
        
        // Display notification about logging out and back in
        setTimeout(() => {
          this.$bvToast.toast('Changes will take effect after users log out and log back in', {
            title: 'Important Note',
            variant: 'info',
            solid: true,
            toaster: 'b-toaster-top-center',
            appendToast: true,
            autoHideDelay: 8000
          });
        }, 500);
        
        this.showAdd = false;
        this.getEmployees({
          sqlQuery: `select e.*, r.Role from teamio.employees e, teamio.roles r where e.SiteID='${this.siteID}' and e.RoleID = r.RoleID;`
        });
        
      } catch (error) {
        console.dir(error);
        if(error.response && error.response.data)
          this.$toast.error(error.response.data);
        else
          this.$toast.error(error);
      } finally {
        this.isLoading = false;
      }      
    },

    async showModal(name, employeeID) {
      // If trying to open delete modal, check permissions
      if (name === 'delete' && !this.$can('delete', 'employee')) {
        this.$toast.error('You do not have permission to delete employees');
        return;
      }
      
      // Special handling for delete: show modal first, then check
      if (name === 'delete' && employeeID) {
        this.selectedEmployeeID = employeeID;
        this.employeeToDelete = this.teamioEmployees.find(emp => emp.EmployeeID === employeeID);
        
        // Show the modal FIRST so progress indicator is visible
        this.$refs['deleteEmployeeWarning'].show();
        
        // Then run checks (user will see progress in the modal)
        await this.$nextTick(); // Wait for modal to render
        await this.checkTasksBeforeDelete();
        
        // Setup scroll detection for modal body
        this.$nextTick(() => {
          this.setupModalScrollDetection();
        });
        return;
      }
      
      this.selectedEmployeeID = employeeID;
      this.$refs[name].show();
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    // Setup scroll detection for modal body
    setupModalScrollDetection() {
      const modalBody = document.querySelector('#deleteEmployeeWarning .modal-body');
      if (modalBody) {
        // Check if modal has scrollable content
        this.checkModalScroll();
        
        // Add scroll listener
        modalBody.addEventListener('scroll', this.handleModalScroll);
      }
    },

    // Handle modal body scroll
    handleModalScroll() {
      this.checkModalScroll();
    },

    // Check if modal body has scrollable content below
    checkModalScroll() {
      const modalBody = document.querySelector('#deleteEmployeeWarning .modal-body');
      if (modalBody) {
        const scrollTop = modalBody.scrollTop;
        const scrollHeight = modalBody.scrollHeight;
        const clientHeight = modalBody.clientHeight;
        
        // Show indicator if there's more content below (with 50px threshold)
        this.showModalScrollIndicator = (scrollHeight - scrollTop - clientHeight) > 50;
      }
    },

    employeeCustomLabel({FirstName, LastName, UserName}) {
      return `${FirstName} ${LastName} (${UserName})`;
    },

    // Which roles get the brand-toned Role pill in the employees table. Presentation
    // only — it changes no permission, it just makes "who can change access here?"
    // answerable at a glance. Matches the same two role names the Feature Permissions
    // card gates on above.
    isPrivilegedRole(role) {
      return role === 'Admin' || role === 'Super Admin';
    },

    openEditModal(employee) {
      // Check if user has permission to update employees
      if (!this.$can('update', 'employee')) {
        this.$toast.error('You do not have permission to edit employees');
        return;
      }
      
      this.editEmployee = {...employee};
      this.editRole = this.roles.find(role => role.RoleID === employee.RoleID);
      this.$refs.editModal.show();
    },

    async updateEmployee() {
      // Double-check permission
      if (!this.$can('update', 'employee')) {
        this.$toast.error('You do not have permission to update employees');
        return false;
      }
      
      if (!this.editRole) {
        this.$toast.error('Please select a Role for this employee');
        return false;
      }

      try {
        this.isLoading = true;
        this.loadingTitle = 'Updating Employee Role';
        this.loadingMessage = 'Please wait while we update the employee information...';
        
        const payload = {
          siteID: this.siteID,
          employeeID: this.editEmployee.EmployeeID,
          roleID: this.editRole.RoleID
        };

        // Call the Vuex action explicitly using dispatch to avoid recursion
        await this.$store.dispatch('myaccount/updateEmployee', payload);
        
        this.$toast.success('Employee updated successfully');
        
        // Display notification about logging out and back in
        setTimeout(() => {
          this.$bvToast.toast('Role changes will take effect after the user logs out and logs back in', {
            title: 'Important Note',
            variant: 'info',
            solid: true,
            toaster: 'b-toaster-top-center',
            appendToast: true,
            autoHideDelay: 8000
          });
        }, 500);
        
        this.hideModal('editModal');
        
        // Refresh employee list
        this.loadingTitle = 'Refreshing Employee List';
        this.loadingMessage = 'Please wait while we update the employee list...';
        await this.init();
      } catch (error) {
        console.error(error);
        if(error.response && error.response.data)
          this.$toast.error(error.response.data);
        else
          this.$toast.error('Error updating employee');
      } finally {
        this.isLoading = false;
      }
    },

    getFeaturePermission(role, feature) {
      if (!this.featurePermissions || !this.featurePermissions[role]) {
        return 'None';
      }
      return this.featurePermissions[role][feature] || 'None';
    },

    setFeaturePermission(role, feature, level) {
      if (!this.featurePermissions[role]) {
        this.$set(this.featurePermissions, role, {});
      }
      this.$set(this.featurePermissions[role], feature, level);
    },

    // Compute the hardcoded default level for one (role, feature) pair, ignoring
    // any saved FEATURE_PERMISSIONS overrides. Used both for rendering the UI
    // and for diff-saving (so future default changes flow through to sites that
    // never touched a particular cell).
    computeDefaultLevel(role, featureKey) {
      const mockUser = { Role: role, EmployeeID: 'mock' };
      // Strip FEATURE_PERMISSIONS so we get the pre-override default.
      const settingsForDefaults = { ...(this.allSettings || {}) };
      delete settingsForDefaults.FEATURE_PERMISSIONS;
      const ability = defineAbilitiesFor(mockUser, settingsForDefaults);
      if (!ability || typeof ability.can !== 'function') return 'None';

      if (ability.can('delete', featureKey)) return 'Delete';
      if (ability.can('update', featureKey) || ability.can('edit', featureKey) || ability.can('add', featureKey)) return 'Update';
      if (ability.can('view', featureKey) || ability.can('read', featureKey)) return 'Read';
      return 'None';
    },

    // Clamp a level value to the closest allowed level for a feature.
    // Used when a previously-saved override (or computed default) refers to a
    // level that isn't in `feature.levels` anymore — e.g., admin once saved
    // 'Update' for Chat, then we restricted Chat to None/Read only. Rather
    // than render an unmatched <option>, downgrade to the nearest allowed
    // level that's still <= the original. Ordering: None < Read < Update < Delete.
    clampLevelToAllowed(level, allowedLevels) {
      if (!Array.isArray(allowedLevels) || allowedLevels.length === 0) return 'None';
      if (allowedLevels.includes(level)) return level;
      const ORDER = ['None', 'Read', 'Update', 'Delete'];
      const targetIdx = ORDER.indexOf(level);
      if (targetIdx < 0) return allowedLevels[0];
      // Walk down from targetIdx looking for an allowed level (preserves intent).
      for (let i = targetIdx; i >= 0; i--) {
        if (allowedLevels.includes(ORDER[i])) return ORDER[i];
      }
      return allowedLevels[0];
    },

    async saveFeaturePermissions() {
      this.isSavingFeaturePermissions = true;
      this.featurePermissionsSaved = false;
      this.featurePermissionsError = false;

      try {
        // Persist only cells that DIFFER from the current default. Cells equal to
        // the default fall through to ability.js at runtime — that way if defaults
        // change in code later, sites that never touched a cell pick up the new value.
        const roles = (this.roles || [])
          .filter(r => !['Admin', 'Super Admin'].includes(r.Role))
          .map(r => r.Role);

        const diff = {};
        roles.forEach(role => {
          const roleDiff = {};
          this.featureList.forEach(feature => {
            const current = this.getFeaturePermission(role, feature.key);
            const def = this.computeDefaultLevel(role, feature.key);
            if (current !== def) roleDiff[feature.key] = current;
          });
          if (Object.keys(roleDiff).length > 0) diff[role] = roleDiff;
        });

        const serialized = JSON.stringify(diff);
        const payload = {
          settingsFor: 'FEATURE_PERMISSIONS',
          settings: { FEATURE_PERMISSIONS: serialized },
          siteID: this.siteID,
        };

        await this.$store.dispatch('customer/upsertSettings', payload);

        this.featurePermissionsSaved = true;
        if (this.allSettings && this.allSettings.FEATURE_PERMISSIONS) {
          this.allSettings.FEATURE_PERMISSIONS.PropertyValue = serialized;
        } else if (this.allSettings) {
          this.$set(this.allSettings, 'FEATURE_PERMISSIONS', { Property: 'FEATURE_PERMISSIONS', PropertyValue: serialized });
        }

        setTimeout(() => {
          this.$bvToast.toast('Permission changes have been saved successfully. These changes will take effect after affected users log out and log back into TeamIO.', {
            title: 'Permissions Updated',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-center',
            appendToast: true,
            autoHideDelay: 10000
          });
        }, 500);
      } catch (e) {
        console.error('Error saving permissions:', e);
        this.featurePermissionsError = true;
      } finally {
        this.isSavingFeaturePermissions = false;
        setTimeout(() => {
          this.featurePermissionsSaved = false;
        }, 2000);
      }
    },

    loadFeaturePermissions() {
      if (!defineAbilitiesFor || typeof defineAbilitiesFor !== 'function') {
        console.error('defineAbilitiesFor is not available or not a function:', defineAbilitiesFor);
        return;
      }

      const roles = (this.roles || [])
        .filter(r => !['Admin', 'Super Admin'].includes(r.Role))
        .map(r => r.Role);

      // Parse saved overrides (sparse — only cells that differ from defaults).
      const fpSetting = this.allSettings && this.allSettings.FEATURE_PERMISSIONS
        ? this.allSettings.FEATURE_PERMISSIONS.PropertyValue : null;
      let overrides = {};
      if (fpSetting) {
        try { overrides = JSON.parse(fpSetting) || {}; }
        catch (e) { console.warn('Error parsing FEATURE_PERMISSIONS:', e); overrides = {}; }
      }

      // Full matrix = defaults + overrides on top, each cell clamped to the
      // feature's allowed level set (handles legacy saved values that no longer
      // match the current featureList configuration).
      const result = {};
      roles.forEach(role => {
        result[role] = {};
        const roleOverrides = (overrides && overrides[role]) || {};
        this.featureList.forEach(feature => {
          const raw = Object.prototype.hasOwnProperty.call(roleOverrides, feature.key)
            ? roleOverrides[feature.key]
            : this.computeDefaultLevel(role, feature.key);
          result[role][feature.key] = this.clampLevelToAllowed(raw, feature.levels);
        });
      });

      this.featurePermissions = result;
    },
  },
  watch: {
    allSettings: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.init();
        }
      }
    },
    roles: {
      immediate: true,
      handler(newRoles) {
        if (newRoles && newRoles.length > 0) {
          this.loadFeaturePermissions();
        }
      }
    },

    // Keep the URL query in sync with the active tab so a tab is shareable /
    // reload-stable. Guard against re-navigating to the same tab (vue-router 3
    // throws NavigationDuplicated otherwise; the .catch swallows it defensively).
    activeTab(idx) {
      const tab = TAB_KEYS[idx] || TAB_KEYS[0];
      const current = this.$route && this.$route.query ? this.$route.query.tab : undefined;
      if (current === tab) return;
      this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {});
    },

    // React to external query changes (e.g. the header pill's Buy-credits link
    // pointing at ?tab=credits while already on this page).
    '$route.query.tab'() {
      this.syncTabFromRoute();
    },

    // Cold-boot deep link into ?tab=whatsapp: syncTabFromRoute refuses that key while
    // `user` is still loading (showWhatsAppTab false). When visibility arrives, re-sync
    // once so an admin's deep link still lands. Idempotent for every other tab key.
    showWhatsAppTab(nowVisible) {
      if (nowVisible) this.syncTabFromRoute();
    },
  }

};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss" scoped>
/* The shared My Account tab design language. Team is skinned from the SAME
   tokens Patient Payments and Insurance Verification already ship, so the tabs
   read as one product (2026-08-18 "Option A" re-skin — see _tokens.scss). */
@import './components/PatientPayments/tokens';

/* ============================================
   PRACTICE LOGO — hard size cap
   Same band the other LogoUrl surfaces use (forms 280x80, verify/emails 200x80).
   object-fit: contain + auto dimensions so a wide wordmark and a square mark both
   fit inside the box without distortion. Scoped: cannot restyle any other page.
   ============================================ */
.site-logo-wrap {
  text-align: center;
  margin-bottom: 12px;
}

.site-logo {
  max-width: 240px;
  max-height: 100px;
  width: auto;
  height: auto;
  object-fit: contain;
}

/* ============================================
   TABS — Teamio design-system underline treatment
   Applies to all three tabs (Team · Credits · Patient Payments) so the header
   reads as one control rather than the Payments tab looking bolted on.

   BootstrapVue renders its own .nav-tabs markup, which doesn't inherit this
   component's scope attribute — so we pierce with ::v-deep, kept UNDER the
   .my-account-tabs ancestor so containment holds and this can't restyle tabs
   on any other page.
   ============================================ */
.my-account-tabs ::v-deep .nav-tabs {
  display: flex;
  align-items: center;
  gap: 26px;
  border-bottom: 1px solid $pp-border;
  margin-bottom: 22px;
}

.my-account-tabs ::v-deep .nav-tabs .nav-item {
  margin-bottom: 0;
}

.my-account-tabs ::v-deep .nav-tabs .nav-link {
  padding: 0 2px 12px;
  border: 0;
  border-radius: 0;
  background: none;
  font-size: 14.5px;
  font-weight: 700;
  color: $pp-text-subtle;
  transition: color 0.12s ease, box-shadow 0.12s ease;
}

.my-account-tabs ::v-deep .nav-tabs .nav-link:hover {
  color: $pp-text-strong;
}

.my-account-tabs ::v-deep .nav-tabs .nav-link.active {
  color: $pp-text;
  background: none;
  /* inset rather than border-bottom so the tab text doesn't shift 2px when it
     becomes active */
  box-shadow: inset 0 -2px 0 $pp-brand;
}

/* ============================================
   MY ACCOUNT CARD SHELL (matches SmsCreditsTab .sct-card)
   ============================================ */
.ma-team {
  max-width: 1600px;
}

/* ============================================
   TAB HEADER (.ma-tabhead) — eyebrow / display title / lede
   The same three-part block PatientPaymentsTab renders as .pp-eyebrow / .pp-title /
   .pp-lede, at TAB scale. The one deliberate deviation from Patient Payments is the
   title size: 34px is the PAGE scale and this sits under the route's own "My Account"
   <h2>, so 24px keeps the tab identified without two competing headlines.

   The class name is shared VERBATIM with SmsCreditsTab.vue on purpose — both blocks are
   the same component of the same design language, so a future change is one find across
   both files. They stay two scoped copies rather than one global rule because this repo's
   CSS is scoped by default and a global .ma-tabhead would be live on every page.
   ============================================ */
.ma-tabhead {
  margin-bottom: 20px;
}

.ma-tabhead__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $pp-brand;
  margin-bottom: 8px;
}

.ma-tabhead__title {
  margin: 0;
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 24px;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: $pp-text;
}

.ma-tabhead__lede {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: $pp-text-muted;
  max-width: 660px;
}

.ma-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
}

.ma-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
}

.ma-card__head-text {
  flex: 1 1 auto;
  min-width: 0;
}

.ma-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $pp-text-strong;
}
.ma-card__title .la {
  color: $pp-brand;
}

.ma-card__subtitle {
  font-size: 0.8rem;
  color: $pp-text-muted;
  margin-top: 2px;
}

.ma-card__action {
  flex-shrink: 0;
  font-weight: 600;
}

.mb-4 {
  width: 100%;
  min-width: 0;
}

/* Ensure proper layout for phone number section */
.border-0.mb-4.mt-5 {
  width: 100%;
  min-width: 0;
}

.border-0.mb-4.mt-5 .mb-4 {
  width: 100%;
  min-width: 0;
}

/* Fix for all text-center containers */
.text-center.py-4 {
  width: 100%;
  min-width: 300px;
  display: block;
}

.text-center.py-4 h6,
.text-center.py-4 p {
  white-space: normal;
  word-wrap: break-word;
  display: block;
  width: 100%;
  text-align: center;
}

/* Feature Permissions Card Styles */
.feature-permissions-card {
  position: relative;
  isolation: isolate;
  contain: layout style;
}

.feature-permissions-wrapper {
  position: relative;
  border: 1px solid $pp-border;
  border-radius: $pp-radius-sm;
  background-color: $pp-surface;
  /* Grow to fill the card's height so this column matches the Employees column
     (both cards are h-100 in the row). flex-basis:0 keeps the tall feature list from
     driving the row height; min-height is a usable floor for short employee lists. */
  flex: 1 1 0;
  min-height: 320px;
  /* One scroll container: the sticky header pins on vertical scroll, and on narrow
     screens the whole table scrolls horizontally (header + body always aligned). */
  overflow: auto;
}

.feature-permissions-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.feature-permissions-wrapper::-webkit-scrollbar-track {
  background: $pp-surface-sunken;
  border-radius: 4px;
}
.feature-permissions-wrapper::-webkit-scrollbar-thumb {
  background: $pp-border-strong;
  border-radius: 4px;
}
.feature-permissions-wrapper::-webkit-scrollbar-thumb:hover {
  background: $pp-text-subtle;
}

.feature-permissions-actions {
  clear: both;
  position: relative;
  z-index: 1;
}

/* Single permissions table with a STICKY header row (one scroll container) */
.feature-permissions-table {
  margin-bottom: 0;
  width: 100%;
  /* = the column min-widths: Feature(150) + 3 roles(95 each). Fills wider cards via
     width:100%; below this the wrapper scrolls horizontally so every role column stays
     reachable (mobile-safe). Matching the column sum avoids a stray scrollbar when the
     card is only just wide enough (the ~1200px side-by-side breakpoint). */
  min-width: 435px;
}

.feature-permissions-table th,
.feature-permissions-table td {
  vertical-align: middle;
  padding: 0.75rem 0.5rem;
  border-color: $pp-border;
}

/* Sticky header — stays pinned while the body scrolls vertically. box-shadow (not
   border-bottom) draws the divider so it survives the sticky scroll in every browser.
   Flat sunken surface + quiet uppercase label: the same table language as
   PaymentTable's .pp-head, so the two tabs' tables read identically. */
.feature-permissions-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: $pp-surface-sunken;
  color: $pp-text-muted;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  box-shadow: inset 0 -1px 0 $pp-border;
}

.feature-permissions-table thead th:first-child {
  text-align: left;
  min-width: 150px;
}

.feature-permissions-table thead th:not(:first-child) {
  text-align: center;
  min-width: 95px;
}

.feature-permissions-table tbody td:first-child {
  text-align: left;
  font-weight: 500;
  min-width: 150px;
  background-color: $pp-surface-sunken;
  color: $pp-text-strong;
}

.feature-permissions-table tbody td:not(:first-child) {
  text-align: center;
  min-width: 95px;
}

.feature-permissions-table select {
  width: 100%;
  border: 1px solid $pp-border-strong;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  color: $pp-text-strong;
}

.feature-permissions-table select:focus {
  border-color: $pp-brand;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  outline: none;
}



/* Employee Edit Form Styles */
.employee-edit-form {
  padding: 0;
}

.employee-edit-form .form-group {
  margin-bottom: 1.5rem;
}

/* ============================================
   EMPLOYEES TABLE SECTION

   The .employees-section / .section-header / .section-title / .section-subtitle /
   .employees-table-card / .action-btn rules that used to live here were DEAD —
   none of those classes exists in this component's template (the card is
   .ma-card, the buttons are .ma-action-btn). Deleted 2026-08-18 with the
   design-language re-skin rather than recoloured, so nobody re-skins CSS that
   nothing renders.
   ============================================ */

/* Table Styling */
.employees-table table {
  margin-bottom: 0 !important;
}

/* Flat sunken header with a quiet uppercase label — the same table language as
   PaymentTable's .pp-head. The old blue gradient made this one table shout
   louder than every other table in My Account. */
.employees-table thead th {
  background: $pp-surface-sunken !important;
  color: $pp-text-muted !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.07em;
  padding: 0.75rem 1rem !important;
  border: none !important;
  border-bottom: 1px solid $pp-border !important;
  position: sticky;
  top: 0;
  z-index: 10;
}

.employees-table tbody tr {
  transition: background-color 0.12s ease;
  border-bottom: 1px solid $pp-border;
}

/* Hover RECOLOURS, it does not move. The row used to slide 4px right and grow a
   coloured shadow, which made a static list feel like a game board and shifted
   the text under the pointer. */
.employees-table tbody tr:hover {
  background-color: $pp-surface-sunken !important;
}

.employees-table tbody td {
  padding: 0.75rem 1rem !important;
  vertical-align: middle !important;
  font-size: 0.875rem;
  color: $pp-text-strong;
}

/* Name Column */
.employees-table tbody td:first-child {
  font-weight: 600;
  color: $pp-text;
}

/* Username Column — mono, quiet: it's an identifier, not prose. Same face and
   tone as PaymentTable's .pp-chart / .pp-txn. */
.employees-table tbody td:nth-child(2) {
  color: $pp-text-subtle;
  font-family: $pp-font-mono;
  font-size: 0.85rem;
}

/* Role Column — the value renders as a .ma-pill (see the Role slot). */
.employees-table tbody td:nth-child(3) {
  font-weight: 500;
}

/* Role pill — .pp-pill's shape and tonal triplets. Admin and Super Admin are the
   roles that can change other people's access, so they carry the brand tone; every
   other role stays neutral rather than competing for attention. */
.ma-pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 4px 10px;
  border-radius: $pp-radius-pill;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid $pp-border;
  background: $pp-surface-sunken;
  color: $pp-text-strong;
}

.ma-pill--ok {
  background: $pp-brand-subtle;
  border-color: $pp-brand-border;
  color: $pp-brand-deep;
}

/* Modern icon action buttons (Employees table) */
/* Keep the two icon buttons on ONE line — the auto-sized Actions column is only
   ~68px and two 32px buttons + a 6px gap would otherwise wrap to two rows in a
   narrow (side-by-side) card. table-responsive scrolls if ever truly too wide. */
.ma-actions-cell {
  white-space: nowrap;
}

.ma-action-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid $pp-border;
  background-color: $pp-surface;
  color: $pp-text-muted;
  font-size: 1rem;
  line-height: 1;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}

.ma-action-btn + .ma-action-btn {
  margin-left: 6px;
}

.ma-action-btn .la {
  pointer-events: none;
}

.ma-action-btn:hover,
.ma-action-btn:focus {
  color: $pp-brand;
  background-color: $pp-brand-subtle;
  border-color: $pp-brand-border;
  box-shadow: none;
}

.ma-action-btn:active {
  color: $pp-brand-deep;
  background-color: $pp-brand-subtle;
}

.ma-action-btn--danger:hover,
.ma-action-btn--danger:focus {
  color: $pp-red;
  background-color: $pp-red-subtle;
  border-color: $pp-red-border;
}

.ma-action-btn--danger:active {
  color: $pp-red-text;
  background-color: $pp-red-subtle;
}

/* VueTables Pagination */
.employees-table .VuePagination {
  padding: 1rem;
  background-color: $pp-surface-sunken;
  border-top: 1px solid $pp-border;
}

.employees-table .VuePagination nav {
  display: flex;
  justify-content: center;
  align-items: center;
}

.employees-table .VuePagination .pagination {
  margin-bottom: 0;
}

.employees-table .VuePagination .page-link {
  color: $pp-brand;
  border: 1px solid $pp-border;
  margin: 0 0.25rem;
  border-radius: 6px;
  transition: background-color 0.12s ease, border-color 0.12s ease;
  font-weight: 500;
}

.employees-table .VuePagination .page-link:hover {
  background-color: $pp-brand-subtle;
  border-color: $pp-brand-border;
}

.employees-table .VuePagination .page-item.active .page-link {
  background: $pp-brand;
  border-color: $pp-brand;
  color: #fff;
}

/* VueTables Search */
.employees-table .VueTables__search {
  padding: 1rem;
  background-color: $pp-surface-sunken;
  border-bottom: 1px solid $pp-border;
}

.employees-table .VueTables__search-field {
  position: relative;
}

.employees-table .VueTables__search-field input {
  width: 100%;
  max-width: 300px;
  padding: 0.6rem 0.75rem 0.6rem 2.5rem;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  font-size: 0.85rem;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.employees-table .VueTables__search-field input:focus {
  border-color: $pp-brand;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  outline: none;
}

.employees-table .VueTables__search-field::before {
  content: '\f002';
  font-family: 'FontAwesome';
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: $pp-text-muted;
  font-size: 0.9rem;
}

/* VueTables Limit */
.employees-table .VueTables__limit {
  padding: 1rem;
  background-color: $pp-surface-sunken;
  border-bottom: 1px solid $pp-border;
}

.employees-table .VueTables__limit-field select {
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid $pp-border-strong;
  border-radius: 6px;
  font-size: 0.85rem;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  background-color: $pp-surface;
}

.employees-table .VueTables__limit-field select:focus {
  border-color: $pp-brand;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  outline: none;
}

/* Empty State */
.employees-table .VueTables__no-results {
  padding: 2rem 1rem;
  text-align: center;
  color: $pp-text-muted;
  font-size: 0.95rem;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .employees-table thead th,
  .employees-table tbody td {
    padding: 0.6rem 0.75rem !important;
    font-size: 0.8rem;
  }
}

/* Modal Layout Fixes */
.modal-dialog {
  max-width: 600px;
}

.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-header {
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem;
}

.modal-title {
  font-weight: 600;
  color: #495057;
  font-size: 1.25rem;
}

.modal-body {
  padding: 1.5rem;
  background-color: #fff;
}

.modal-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

/* Form Controls */
.form-control {
  border-radius: $pp-radius-sm;
  border: 1px solid $pp-border-strong;
  padding: 0.6rem 0.8rem;
  font-size: 0.875rem;
  color: $pp-text-strong;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.form-control:focus {
  border-color: $pp-brand;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  outline: none;
}

.form-control:disabled {
  background-color: $pp-surface-sunken;
  color: $pp-text-muted;
  border-color: $pp-border;
}

/* Button Styling */
.btn {
  border-radius: $pp-radius-sm;
  font-weight: 600;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.btn-primary {
  background-color: $pp-brand;
  border-color: $pp-brand;
  color: #fff;
}

.btn-primary:hover {
  background-color: $pp-brand-deep;
  border-color: $pp-brand-deep;
}

.btn-success {
  background-color: $pp-brand;
  border-color: $pp-brand;
  color: #fff;
}

.btn-success:hover {
  background-color: $pp-brand-deep;
  border-color: $pp-brand-deep;
}

.btn-outline-secondary {
  border-color: $pp-border-strong;
  color: $pp-text-strong;
}

.btn-outline-secondary:hover {
  background-color: $pp-surface-sunken;
  border-color: $pp-border-strong;
  color: $pp-text-strong;
}

.btn-outline-danger {
  border-color: $pp-red;
  color: $pp-red;
}

.btn-outline-danger:hover {
  background-color: $pp-red;
  border-color: $pp-red;
  color: #fff;
}

/* Card Styling */
.card {
  border-radius: $pp-radius;
  border: 1px solid $pp-border;
  box-shadow: $pp-shadow;
}

.card-header {
  background-color: $pp-surface-sunken;
  border-bottom: 1px solid $pp-border;
  padding: 1rem 1.5rem;
}

/* Alert Styling — flat tonal fills (the .pp-pill triplets), not gradients, and
   no hover lift: an alert is a statement, not a control. */
.alert {
  border-radius: $pp-radius-sm;
  border: 1px solid transparent;
  padding: 14px 18px;
  font-size: 0.9rem;
}

.alert-info {
  background: $pp-brand-subtle;
  color: $pp-text-strong;
  border-color: $pp-brand-border;
  border-left: 4px solid $pp-brand;
}

.alert-info strong {
  color: $pp-brand-deep;
  font-weight: 700;
}

.alert-info i {
  color: $pp-brand;
  font-size: 1.1rem;
  margin-right: 8px;
}

.alert-success {
  background: $pp-brand-subtle;
  color: $pp-text-strong;
  border-color: $pp-brand-border;
  border-left: 4px solid $pp-brand;
}

.alert-success strong {
  color: $pp-brand-deep;
  font-weight: 700;
}

.alert-success i {
  color: $pp-brand;
  font-size: 1.1rem;
  margin-right: 8px;
}

.alert-warning {
  background: $pp-amber-subtle;
  color: $pp-amber-text;
  border-color: $pp-amber-border;
  border-left: 4px solid $pp-amber;
}

.alert-warning strong {
  color: $pp-amber-text;
  font-weight: 700;
}

.alert-warning i {
  color: $pp-amber;
  font-size: 1.1rem;
  margin-right: 8px;
}

/* Explicit rather than inherited: `.alert { border: 1px solid transparent }` above is
   more specific than Bootstrap's own `.alert-danger`, so without this the danger alert
   would lose its outline. (The rule this replaced said `border: none`, which dropped it
   entirely.) */
.alert-danger {
  background: $pp-red-subtle;
  color: $pp-red-text;
  border-color: $pp-red-border;
  border-left: 4px solid $pp-red;
}

.alert-danger strong {
  color: $pp-red-text;
  font-weight: 700;
}

.alert-danger i {
  color: $pp-red;
  font-size: 1.1rem;
  margin-right: 8px;
}

/* Global modal text fixes */
.modal .text-center.py-4 {
  width: 100% !important;
  min-width: 300px !important;
  display: block !important;
}

.modal .text-center.py-4 h6,
.modal .text-center.py-4 p {
  white-space: normal !important;
  word-wrap: break-word !important;
  display: block !important;
  width: 100% !important;
  text-align: center !important;
}

.modal .text-muted {
  white-space: normal !important;
  word-wrap: break-word !important;
  display: inline-block !important;
  width: auto !important;
}

.modal small.text-muted {
  white-space: normal !important;
  word-wrap: break-word !important;
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
}

.modal .mb-4 {
  width: 100% !important;
  min-width: 0 !important;
}

.modal .d-flex {
  width: 100% !important;
  min-width: 0 !important;
}

/* Additional fixes for text layout issues */
.bg-light.rounded {
  width: 100% !important;
  min-width: 300px !important;
  display: block !important;
  flex-direction: column !important;
}

.bg-light.rounded h6,
.bg-light.rounded p {
  white-space: normal !important;
  word-wrap: break-word !important;
  display: block !important;
  width: 100% !important;
  text-align: center !important;
  margin-left: auto !important;
  margin-right: auto !important;
}

/* Ensure card content has proper width */
.card .mb-4 {
  width: 100% !important;
  min-width: 0 !important;
  flex: 1 !important;
}

/* Fix modal text layout issues */
.teamio-modal-content .text-center.py-4 {
  width: 100% !important;
  min-width: 300px !important;
  display: block !important;
}

.teamio-modal-content .text-center.py-4 h6,
.teamio-modal-content .text-center.py-4 p {
  white-space: normal !important;
  word-wrap: break-word !important;
  display: block !important;
  width: 100% !important;
  text-align: center !important;
}

.teamio-modal-content .text-muted {
  white-space: normal !important;
  word-wrap: break-word !important;
  display: inline-block !important;
  width: auto !important;
}

.teamio-modal-content small.text-muted {
  white-space: normal !important;
  word-wrap: break-word !important;
  display: block !important;
  width: 100% !important;
}

/* Badge Styling — pill shape + the tonal triplets, matching .pp-pill/.ma-pill */
.badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.4rem 0.8rem;
  border-radius: $pp-radius-pill;
  border: 1px solid transparent;
}

.badge-success,
.badge-info {
  background-color: $pp-brand-subtle;
  border-color: $pp-brand-border;
  color: $pp-brand-deep;
}

.badge-warning {
  background-color: $pp-amber-subtle;
  border-color: $pp-amber-border;
  color: $pp-amber-text;
}

.badge-danger {
  background-color: $pp-red-subtle;
  border-color: $pp-red-border;
  color: $pp-red-text;
}

.badge-secondary {
  background-color: $pp-surface-sunken;
  border-color: $pp-border;
  color: $pp-text-strong;
}

/* Utility Classes */
.cursor-pointer {
  cursor: pointer;
}

.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style lang="scss">
/* ⚠️ This block is UNSCOPED and MyAccount.vue is imported statically by Routes.js,
   so everything here is live on EVERY page of the app, always.
   The 2026-08-18 design-language re-skin therefore only retoned the selectors that
   are MyAccount-EXCLUSIVE (.teamio-*, .number-option, .step-*, .verification-*,
   .task-preview-link, .task-list-*, .scroll-*, #deleteEmployeeWarning …). The
   app-wide Bootstrap overrides further down (.btn-primary, .form-control,
   .custom-radio, .multiselect__*, .toast.*, .alert-info, .modal-*) were left on the
   app's primary blue ON PURPOSE — recolouring them would have re-skinned every page
   in Teamio, which is not what this change is. Untangling that (scoping them to
   MyAccount, or promoting them to the theme) is separate, tracked work. */
@import './components/PatientPayments/tokens';

/* Global styles for modal components that render at body level */
.teamio-modal {
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
}

.teamio-modal .modal-content {
  border: none;
  border-radius: 8px;
}

.teamio-modal .modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem;
  background-color: #fff;
}

.teamio-modal .modal-title {
  font-weight: 600;
  color: #495057;
  font-size: 1.25rem;
}

.teamio-modal .modal-body {
  padding: 1.5rem;
  background-color: #fff;
}

.teamio-modal .modal-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.teamio-modal-content {
  background-color: white;
  border-radius: 0 0 8px 8px;
}

.teamio-modal-subtitle {
  color: #495057;
  font-weight: 500;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
}

.teamio-input {
  border-radius: 0.3rem;
  border: 1px solid #d6dee5;
  padding: 0.6rem 0.8rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-size: 0.875rem;
}

.teamio-input:focus {
  border-color: $pp-brand;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  outline: none;
}

.teamio-input:disabled {
  background-color: #f8f9fa;
  color: #798892;
  border-color: #e9ecef;
}

.teamio-form-label {
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
  font-size: 0.875rem;
}

.teamio-modal-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #f1f1f1;
}

.available-numbers-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.3rem;
  padding: 0.5rem;
  background-color: #fff;
}

.number-option {
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid #e9ecef;
}

.number-option:hover {
  background-color: $pp-surface-sunken;
  border-color: $pp-brand-border;
}

.number-option.selected {
  border-color: $pp-brand;
  background-color: $pp-brand-subtle;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
}

.number-option.selected .fa-check-circle {
  color: $pp-brand;
}

/* Delete Employee Verification Steps */
.verification-steps {
  background: linear-gradient(135deg, #f8f9fb 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.verification-step {
  margin-bottom: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #f0f2f5;
}

.verification-step:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.verification-step:last-child {
  margin-bottom: 0;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.step-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.step-content {
  flex: 1;
}

.step-content strong {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
}

.step-checking .step-icon {
  color: $pp-brand;
  background: $pp-brand-subtle;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.step-complete .step-icon {
  color: #3abf94;
  background: rgba(58, 191, 148, 0.12);
}

.step-error .step-icon {
  color: #f55d5d;
  background: rgba(245, 93, 93, 0.12);
}

.step-details {
  margin-top: 12px;
  font-size: 0.9rem;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Enhanced Radio Button Styling for Task Handling Options */
.custom-control-input:checked ~ .custom-control-label::before {
  background-color: #547fff;
  border-color: #547fff;
  box-shadow: 0 0 0 3px rgba(84, 127, 255, 0.15);
}

.custom-control-label {
  cursor: pointer;
  transition: all 0.2s ease;
  padding-left: 8px;
}

.custom-control-label:hover {
  color: #547fff;
}

.custom-control-label strong {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
}

.custom-radio .custom-control-label::before {
  border: 2px solid #d6dee5;
  transition: all 0.2s ease;
}

.custom-radio .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #547fff;
  border-color: #547fff;
}

.custom-radio {
  padding: 12px;
  margin-bottom: 8px !important;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.custom-radio:hover {
  background-color: rgba(84, 127, 255, 0.03);
  border-color: rgba(84, 127, 255, 0.15);
}

.custom-radio .custom-control-input:checked ~ .custom-control-label {
  color: #547fff;
}

.custom-radio:has(.custom-control-input:checked) {
  background-color: rgba(84, 127, 255, 0.06);
  border-color: rgba(84, 127, 255, 0.25);
  box-shadow: 0 2px 8px rgba(84, 127, 255, 0.1);
}

/* Employee Deletion Modal Header Styling */
#deleteEmployeeWarning .modal-header {
  background: linear-gradient(135deg, #f8f9fb 0%, #ffffff 100%);
  border-bottom: 2px solid #e9ecef;
}

#deleteEmployeeWarning .modal-title {
  color: #2c3e50;
  font-weight: 700;
  font-size: 1.35rem;
  letter-spacing: -0.02em;
}

#deleteEmployeeWarning .border-bottom {
  background: linear-gradient(135deg, #fef9f8 0%, #fff5f3 100%);
  padding: 18px 20px !important;
  border-radius: 8px;
  border: 1px solid rgba(245, 93, 93, 0.15) !important;
  box-shadow: 0 2px 8px rgba(245, 93, 93, 0.06);
}

#deleteEmployeeWarning .border-bottom p {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.85rem;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

#deleteEmployeeWarning .border-bottom h5 {
  color: #f55d5d;
  font-weight: 700;
  font-size: 1.25rem;
  margin: 0;
}

/* Progress Bar Styling */
#deleteEmployeeWarning .progress {
  background-color: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

#deleteEmployeeWarning .progress-bar {
  background: $pp-brand;
}

#deleteEmployeeWarning .text-center.text-muted.small {
  color: #6c757d !important;
  font-weight: 500;
  margin-top: 12px;
}

/* Modal Footer Button Styling */
#deleteEmployeeWarning .modal-footer {
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  border-top: 2px solid #e9ecef;
  padding: 18px 24px;
}

#deleteEmployeeWarning .btn-secondary {
  background-color: #f8f9fa;
  border: 2px solid #d6dee5;
  color: #495057;
  font-weight: 600;
  padding: 10px 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

#deleteEmployeeWarning .btn-secondary:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: #c1ccd3;
  color: #2c3e50;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

#deleteEmployeeWarning .btn-danger {
  background: linear-gradient(135deg, #f55d5d 0%, #ff7b7b 100%);
  border: none;
  color: white;
  font-weight: 600;
  padding: 10px 28px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(245, 93, 93, 0.3);
}

#deleteEmployeeWarning .btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #e84a4a 0%, #ff6868 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(245, 93, 93, 0.4);
}

#deleteEmployeeWarning .btn-danger:disabled {
  background: #d6dee5;
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

/* Enhanced List Items in Step Details */
.step-details ul.list-unstyled {
  margin-top: 12px;
}

.step-details ul.list-unstyled li {
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

/* Hover recolours only — no slide. Same rule as the employees table. */
.step-details ul.list-unstyled li:hover {
  background-color: $pp-surface-sunken;
  border-color: $pp-border-strong;
}

.step-details ul.list-unstyled li.text-warning {
  background: linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%);
  border-left: 3px solid #d4980d;
  color: #5d4e37;
}

.step-details ul.list-unstyled li.text-warning i.fa-arrow-right {
  color: #c78a00 !important;
}

.step-details ul.list-unstyled li.text-warning strong {
  color: #8b6f00 !important;
}

.step-details ul.list-unstyled li.text-success {
  background: linear-gradient(135deg, rgba(58, 191, 148, 0.06) 0%, rgba(58, 191, 148, 0.1) 100%);
  border-left: 3px solid #3abf94;
}

.step-details ul.list-unstyled li i.fa-arrow-right {
  color: $pp-brand;
  font-weight: 600;
  margin-right: 8px;
}

.step-details ul.list-unstyled li strong {
  font-weight: 700;
  color: #2c3e50;
}

/* Task list display - ensure visibility */
.task-list-container {
  background-color: #ffffff !important;
}

.task-item {
  font-size: 0.875rem !important;
  line-height: 1.4;
}

.task-content {
  color: #495057 !important;
  font-size: 0.875rem !important;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
}

.task-date {
  font-size: 0.875rem !important;
  color: #6c757d !important;
}

.task-assignee-count {
  font-size: 0.75rem !important;
  color: #6c757d !important;
  margin-top: 0.25rem;
}

/* Task Preview Table Styles - Match Task Page Design */
.task-preview-table {
  font-size: 0.875rem;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  background-color: #ffffff;
  border: none;
  border-collapse: separate;
  border-spacing: 0;
}

.task-preview-table thead {
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.task-preview-table thead th {
  font-weight: 600;
  color: #475569;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 16px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
}

.task-preview-table tbody tr {
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f1f5f9;
}

.task-preview-table tbody tr:last-child {
  border-bottom: none;
}

.task-preview-table tbody tr:hover {
  background-color: #f8fafc;
}

.task-preview-table tbody td {
  padding: 14px 16px;
  vertical-align: middle;
  border: none;
  color: #334155;
  font-size: 0.875rem;
}

.task-preview-table tbody td:first-child {
  color: #64748b;
  font-weight: 500;
  font-size: 0.8125rem;
}

.task-content-cell {
  line-height: 1.6;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.task-text {
  color: #334155;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 6px;
}

.task-assignees {
  color: #64748b;
  font-size: 0.8125rem;
  line-height: 1.4;
}

.task-assignees strong {
  color: #475569;
  font-weight: 600;
  margin-right: 4px;
}

/* Employee Tag Badges (@ mentions in task content) - Match Task Page */
.badge-employee {
  background-color: #3b82f6;
  background-image: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  letter-spacing: 0.01em;
}

.badge-employee i {
  font-size: 0.6875rem;
  margin: 0;
}

/* Patient Tag Badges - Match Task Page */
.badge-patient {
  background-color: #10b981;
  background-image: linear-gradient(135deg, #10b981, #059669);
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  letter-spacing: 0.01em;
}

.badge-patient i {
  font-size: 0.6875rem;
  margin: 0;
}

/* Assignee Badge - Match Task Page */
.badge-assignee {
  background-color: #64748b;
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  letter-spacing: 0.01em;
}

.badge-assignee i {
  font-size: 0.6875rem;
  margin: 0;
}

/* Workflow Item Styling */
.workflow-item {
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #17a2b8;
}

.workflow-item strong {
  color: #2c3e50;
}

/* Enhanced Form Select Dropdown for Employee Assignment */
#deleteEmployeeWarning .b-form-select,
#deleteEmployeeWarning select.form-control {
  border: 2px solid #d6dee5;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.9rem;
  color: #2c3e50;
  background-color: #ffffff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

#deleteEmployeeWarning .b-form-select:focus,
#deleteEmployeeWarning select.form-control:focus {
  border-color: $pp-brand;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  outline: none;
}

#deleteEmployeeWarning label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

/* Modal dialog constraints for proper scrolling */
#deleteEmployeeWarning .modal-dialog {
  max-width: 1140px; /* xl size */
  max-height: calc(100vh - 60px);
  margin: 30px auto;
  display: flex;
  flex-direction: column;
}

#deleteEmployeeWarning .modal-content {
  max-height: calc(100vh - 60px);
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

/* Make modal body scrollable with compact padding */
#deleteEmployeeWarning .modal-body {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  padding: 16px 20px !important;
  min-height: 0 !important;
  max-height: none;
}

/* Custom scrollbar for modal body */
#deleteEmployeeWarning .modal-body::-webkit-scrollbar {
  width: 8px;
}

#deleteEmployeeWarning .modal-body::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 4px;
}

#deleteEmployeeWarning .modal-body::-webkit-scrollbar-thumb {
  background: #d6dee5;
  border-radius: 4px;
}

#deleteEmployeeWarning .modal-body::-webkit-scrollbar-thumb:hover {
  background: #c1ccd3;
}

/* Reduce padding in employee info header */
#deleteEmployeeWarning .border-bottom {
  padding: 12px 16px !important; /* Reduced from 18px 20px */
  margin-bottom: 16px !important; /* Reduced from default */
}

/* Reduce spacing in verification steps */
#deleteEmployeeWarning .verification-steps {
  margin-bottom: 16px !important; /* Reduced from 24px */
}

#deleteEmployeeWarning .verification-step {
  margin-bottom: 10px; /* Reduced from 12px */
  padding: 12px; /* Reduced from 16px */
}

/* Styling for "What would you like to do" section */
#deleteEmployeeWarning .form-group > p {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.05rem;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}

/* Indent task options group */
#deleteEmployeeWarning .task-options-group {
  margin-left: 24px;
  padding-left: 8px;
  border-left: 3px solid #e9ecef;
}

/* Improved radio button styling */
#deleteEmployeeWarning .custom-radio {
  padding: 10px 12px; /* Reduced from 12px */
  border-radius: 8px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  margin-bottom: 8px !important; /* Reduced spacing */
}

#deleteEmployeeWarning .custom-radio:hover {
  background-color: #f8f9fb;
  border-color: #e9ecef;
}

#deleteEmployeeWarning .custom-radio.has-active-input {
  background-color: $pp-brand-subtle;
  border-color: $pp-brand-border;
}

#deleteEmployeeWarning .custom-control-label strong {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

#deleteEmployeeWarning .custom-control-label small {
  color: #6c757d;
  font-size: 0.85rem;
  margin-top: 2px;
}

/* Better employee dropdown styling to match theme */
#deleteEmployeeWarning .b-form-select option,
#deleteEmployeeWarning select.form-control option {
  padding: 10px;
  font-size: 0.9rem;
}

/* Consistent styling for "Preview affected tasks" section at bottom */
#deleteEmployeeWarning .mt-3 > a.task-preview-link {
  display: inline-block;
  padding: 8px 12px;
  background-color: #f8f9fb;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
}

#deleteEmployeeWarning .mt-3 > a.task-preview-link:hover {
  background-color: $pp-brand-subtle;
  border-color: $pp-brand-border;
}

/* Task preview list styling - consistent with top section */
#deleteEmployeeWarning .mt-3 > div[style*="max-height"] {
  max-height: 150px !important; /* Match the top section */
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px !important;
  padding: 10px;
  margin-top: 10px;
}

/* Reduce spacing in alerts */
#deleteEmployeeWarning .alert {
  margin-bottom: 12px !important; /* Reduced from default */
  padding: 12px 16px !important; /* Reduced from 14px 18px */
}

/* Task list container with scroll indicator */
.task-list-container {
  position: relative;
  margin-top: 10px;
}

.task-list-scrollable {
  max-height: 150px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 10px;
  position: relative;
}

.task-list-scrollable::-webkit-scrollbar {
  width: 6px;
}

.task-list-scrollable::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 3px;
}

.task-list-scrollable::-webkit-scrollbar-thumb {
  background: $pp-brand;
  border-radius: 3px;
}

.task-list-scrollable::-webkit-scrollbar-thumb:hover {
  background: $pp-brand-deep;
}

/* Scroll indicator */
.scroll-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(255, 255, 255, 1) 100%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  pointer-events: none;
  border-radius: 0 0 6px 6px;
}

.scroll-indicator i {
  color: $pp-brand;
  font-size: 14px;
  margin-right: 4px;
  animation: bounce 2s infinite;
}

.scroll-indicator span {
  color: $pp-brand;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

/* Modal Body Scroll Indicator */
.modal-scroll-indicator {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  pointer-events: none;
  margin: 0 -20px -20px -20px; /* Extend to edges of modal body */
}

.scroll-hint {
  background: $pp-brand;
  color: #ffffff;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  border-top: 2px solid rgba(255, 255, 255, 0.3);
}

.scroll-hint i {
  font-size: 1rem;
  animation: bounceVertical 1.5s infinite;
}

@keyframes bounceVertical {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(4px);
  }
}

/* Task preview toggle links - custom class to avoid Bootstrap conflict */
.text-success a.task-preview-link,
.text-success a.task-preview-link:link,
.text-success a.task-preview-link:visited,
a.task-preview-link,
a.task-preview-link:link,
a.task-preview-link:visited {
  color: $pp-brand !important;
  text-decoration: none !important;
  font-size: 0.875rem;
  cursor: pointer !important;
  transition: color 0.12s ease;
  display: inline-block;
}

.text-success a.task-preview-link:hover,
.text-success a.task-preview-link:active,
a.task-preview-link:hover,
a.task-preview-link:active {
  color: $pp-brand-deep !important;
  text-decoration: underline !important;
}

.text-success a.task-preview-link:focus,
a.task-preview-link:focus {
  color: $pp-brand !important;
  outline: none;
}

.step-details a {
  color: $pp-brand !important;
  text-decoration: none !important;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.12s ease;
  display: inline-block;
}

.step-details a:hover {
  color: $pp-brand-deep !important;
  text-decoration: underline !important;
}

/* Global multiselect overrides */
.multiselect__tags {
  border-radius: 0.3rem;
  border: 1px solid #d6dee5;
  padding: 0.6rem 0.8rem;
  min-height: 40px;
  font-size: 0.875rem;
}

.multiselect__tags:focus-within {
  border-color: #547fff;
  box-shadow: 0 0 0 3px rgba(84, 127, 255, 0.1);
}

.multiselect__select {
  height: 100%;
}

.multiselect__tag {
  background: #547fff;
  color: white;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.multiselect__tag-icon:hover {
  background: rgba(255, 255, 255, 0.2);
}

.multiselect__option--highlight {
  background: #547fff;
  color: white;
}

.multiselect__option--selected.multiselect__option--highlight {
  background: #3abf94;
  color: white;
}

.multiselect__option--selected {
  background: #f8f9fa;
  color: #495057;
  font-weight: 500;
}

.multiselect__content-wrapper {
  border: 1px solid #d6dee5;
  border-top: none;
  border-radius: 0 0 0.3rem 0.3rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.multiselect__single {
  color: #495057;
  font-size: 0.875rem;
}

.multiselect__placeholder {
  color: #a3aeb7;
  font-size: 0.875rem;
}

/* Toast styling - global because toasts are rendered at body level */
.b-toaster-top-center .toast {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 0.3rem;
  overflow: hidden;
  min-width: 350px;
}

.toast.b-toast-info {
  background-color: rgba(84, 127, 255, 0.05);
  border-left: 4px solid #547fff;
}

.toast.b-toast-info .toast-header {
  background-color: #547fff;
  color: white;
  border-bottom: none;
  padding: 0.75rem 1rem;
}

.toast.b-toast-info .toast-body {
  padding: 1rem;
  color: #495057;
  font-weight: 500;
}

.toast.b-toast-success {
  background-color: rgba(58, 191, 148, 0.05);
  border-left: 4px solid #3abf94;
}

.toast.b-toast-success .toast-header {
  background-color: #3abf94;
  color: white;
  border-bottom: none;
}

.toast.b-toast-error {
  background-color: rgba(245, 93, 93, 0.05);
  border-left: 4px solid #f55d5d;
}

.toast.b-toast-error .toast-header {
  background-color: #f55d5d;
  color: white;
  border-bottom: none;
}

/* Global Bootstrap overrides for consistent TeamIO styling */
.modal-dialog {
  max-width: 600px;
}

.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-header {
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem;
}

.modal-title {
  font-weight: 600;
  color: #495057;
  font-size: 1.25rem;
}

.modal-body {
  padding: 1.5rem;
  background-color: #fff;
}

.modal-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}



.btn {
  border-radius: 0.3rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #547fff;
  border-color: #547fff;
}

.btn-primary:hover {
  background-color: #4169e6;
  border-color: #4169e6;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(84, 127, 255, 0.3);
}

.btn-success {
  background-color: #3abf94;
  border-color: #3abf94;
}

.btn-success:hover {
  background-color: #2da87a;
  border-color: #2da87a;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(58, 191, 148, 0.3);
}

.form-control {
  border-radius: 0.3rem;
  border: 1px solid #d6dee5;
  font-size: 0.875rem;
  color: #495057;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  border-color: #547fff;
  box-shadow: 0 0 0 3px rgba(84, 127, 255, 0.1);
  outline: none;
}

.alert {
  border-radius: 0.3rem;
  border: none;
}

.alert-info {
  background-color: rgba(84, 127, 255, 0.1);
  color: #495057;
  border-left: 4px solid #547fff;
}
</style>